/**
 * Job Form - Shared logic for both modal and full-page job creation/edit forms.
 *
 * Single source of truth. Templates only provide:
 *   1. Django data injection (customers, locations, cleaners, worksites)
 *   2. Translated strings (via {% trans %})
 *   3. Context-specific callbacks
 *
 * All DOM manipulation, event handling, and business logic lives here.
 *
 * PR-6c-2 (2026-05) — every ``ServiceType`` helper below
 * (``fetchServiceTypeDefaults``, ``createServiceTypeHandler``,
 * ``_patchServiceTypeCacheEntry``, ``refreshServiceTypeMovableFlag``,
 * ``refreshAllServiceTypesMovableFlags``,
 * ``updateServiceTypePickerLockState``, the ServiceType branch of
 * ``_resolveTrackingMode`` and ``_syncCiaoSnapshotFields``, plus the
 * ``[name="service_type"]`` query selectors) is now a no-op because
 * the form no longer renders a Service Type picker and the matching
 * backend APIs have been removed. The function signatures + call
 * sites are kept intact so PR-6c-4 can repurpose them for the new
 * ``ContractCategory`` model with minimal churn.
 */

import { createDynamicModal } from '../utils/ui/dynamic-modal.js';
import { initChecklistEditor as initSharedChecklistEditor } from './checklist-editor.js';
import { customerAvatarHtml } from '../utils/ui/customer-avatar.js';
import {
    parseMoney as _parseMoney,
    formatMoney as _formatMoney,
    sanitizeMoneyField as _sanitizeMoneyValue,
} from '../utils/forms/money.js';

// ========================================================
// HELPERS
// ========================================================

export function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

export function getCleanerName(cleaner) {
    if (cleaner.first_name && cleaner.last_name) {
        return `${cleaner.first_name} ${cleaner.last_name}`;
    }
    return cleaner.username;
}

// ========================================================
// SUBCONTRACTOR ELIGIBILITY (FAQ Q41)
// ========================================================
//
// A SUBCONTRACTOR-typed cleaner can only be assigned to a Job whose
// Worksite has the cleaner's employer (``subcontractor_company_id``)
// declared in its active ``SubcontractorAssignment`` set today. The
// server-side gate
// (``apps.compliance.services.find_subcontractor_assignment_gaps_for_cleaners``
// → ``JobForm.clean()`` / ``validate_slot_subcontractor_assignments``)
// stays the authoritative validator; this client-side helper exists
// purely to render an early visual cue (greyed dropdown row + grouped
// hint with a one-click "Declare X" button) so the office never gets
// to a post-submit blocking alert in the normal flow.

export function getDeclaredCompanyIdsForWorksite(worksites, worksiteId) {
    if (!worksites || !worksiteId) return new Set();
    const ws = worksites.find(w => String(w.id) === String(worksiteId));
    if (!ws) return new Set();
    return new Set((ws.declared_subcontractor_ids || []).map(String));
}

export function getWorksiteLabel(worksites, worksiteId) {
    if (!worksites || !worksiteId) return '';
    const ws = worksites.find(w => String(w.id) === String(worksiteId));
    return ws ? ws.label : '';
}

export function resolveCurrentWorksiteId(formId = 'job-form') {
    const sel = document.getElementById('id_work_site');
    if (sel && sel.value) return sel.value;
    const form = document.getElementById(formId);
    return form?.dataset.initialWorkSiteId || '';
}

export function isCleanerEligibleForWorksite(cleaner, declaredCompanyIds, worksites, worksiteId) {
    if (!worksiteId) return true;
    // In-house staff (no ``subcontractor_company_id``) are always
    // eligible. SUBCONTRACTOR-typed cleaners need their employer
    // declared on the selected worksite — if the worksite declares
    // nobody yet, every subcontractor cleaner is ineligible (but we
    // do NOT surface the proactive « declare X » banner on the job
    // form; declare subs from the worksite detail page instead).
    if (!cleaner || !cleaner.subcontractor_company_id) return true;
    if (!declaredCompanyIds || declaredCompanyIds.size === 0) return false;
    return declaredCompanyIds.has(String(cleaner.subcontractor_company_id));
}

// ========================================================
// FLOWBITE TOOLTIP HELPER (per project rule .cursor/rules/flowbite-tooltips.mdc)
// ========================================================
//
// Cleaner picker rows live inside a scrollable dropdown
// (``overflow-y: auto``) — placing the tooltip ``<div role="tooltip">``
// as a direct sibling would clip it at the dropdown's bounds. To dodge
// that, every cleaner-picker tooltip is rendered into a dedicated
// container appended to ``<body>`` so Popper can position it freely.
//
// The container is wiped + repopulated on every dropdown re-render
// (search filter, worksite change, availability fetch). Triggers carry
// ``data-tooltip-target`` and the canonical project helper
// (``window.reinitFlowbiteWidgets``) instantiates the Tooltip on each
// re-render — same idempotent contract as elsewhere in the codebase.

const CLEANER_PICKER_TOOLTIP_CONTAINER_ID = 'cleaner-picker-tooltips';

function getCleanerPickerTooltipContainer() {
    let el = document.getElementById(CLEANER_PICKER_TOOLTIP_CONTAINER_ID);
    if (!el) {
        el = document.createElement('div');
        el.id = CLEANER_PICKER_TOOLTIP_CONTAINER_ID;
        document.body.appendChild(el);
    }
    return el;
}

function clearCleanerPickerTooltips(prefix) {
    const container = getCleanerPickerTooltipContainer();
    container.querySelectorAll(`[data-tooltip-prefix="${prefix}"]`).forEach(el => el.remove());
}

// Append a tooltip ``<div role="tooltip">`` to the body-level container
// and return the unique id the caller should use as
// ``data-tooltip-target`` on the trigger. ``text`` is escaped — never
// pass HTML.
function addCleanerPickerTooltip(prefix, key, text) {
    const tipId = `tip-${prefix}-${key}`;
    const div = document.createElement('div');
    div.id = tipId;
    div.setAttribute('role', 'tooltip');
    div.setAttribute('data-tooltip-prefix', prefix);
    // ``z-[10000]`` keeps the tooltip above the picker dropdown,
    // which ``positionDropdown()`` forces to ``z-index: 9999`` via
    // an inline style so it can escape the form's stacking context.
    // Anything lower (like the conventional Flowbite ``z-10``) ends
    // up rendered *behind* the dropdown panel and visually clipped
    // — the tooltip looks like it's "below" the dropdown content.
    // ``whitespace-normal break-words`` defeats the
    // ``whitespace-nowrap`` inheritance trap (see flowbite-tooltips
    // cursor rule).
    div.className = 'absolute z-[10000] invisible inline-block max-w-xs whitespace-normal break-words px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700';
    div.textContent = text;
    const arrow = document.createElement('div');
    arrow.className = 'tooltip-arrow';
    arrow.setAttribute('data-popper-arrow', '');
    div.appendChild(arrow);
    getCleanerPickerTooltipContainer().appendChild(div);
    return tipId;
}

// ========================================================
// INLINE VALIDATION
// ========================================================

export function clearFieldError(field) {
    if (!field) return;

    const fieldGroup = field.closest('.field-group');
    if (fieldGroup) {
        fieldGroup.classList.remove('has-error');
        const label = fieldGroup.querySelector('label');
        if (label) {
            label.classList.remove('text-red-600', 'dark:text-red-500');
            label.classList.add('text-gray-900', 'dark:text-white');
        }
        fieldGroup.querySelectorAll('.field-error, p.text-red-600, p.text-xs.text-red-600').forEach(msg => msg.remove());
    }

    const td = field.closest('td');
    if (td) {
        td.querySelectorAll('p.text-red-600, p.text-xs.text-red-600, .field-error').forEach(msg => msg.remove());
    }

    const parent = field.closest('.relative') || field.parentElement;
    if (parent && parent.nextElementSibling) {
        const next = parent.nextElementSibling;
        if (next.tagName === 'P' && (next.classList.contains('field-error') || next.classList.contains('text-red-600'))) {
            next.remove();
        }
    }
}

export function clearPickerError(pickerElement) {
    if (!pickerElement) return;
    const fieldGroup = pickerElement.closest('.field-group');
    if (fieldGroup) {
        fieldGroup.classList.remove('has-error');
        const label = fieldGroup.querySelector('label');
        if (label) {
            label.classList.remove('text-red-600', 'dark:text-red-500');
            label.classList.add('text-gray-900', 'dark:text-white');
        }
        fieldGroup.querySelectorAll('.field-error, p.text-red-600').forEach(msg => msg.remove());
    }
    pickerElement.classList.remove('border-red-500', 'dark:border-red-500');
    const border = pickerElement.querySelector('.border-red-500');
    if (border) border.classList.remove('border-red-500', 'dark:border-red-500');
}

function initFormValidation(formId) {
    const form = document.getElementById(formId);
    if (!form) return;

    form.addEventListener('input', function (e) {
        const field = e.target;
        if ((field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') && field.value.trim()) {
            clearFieldError(field);
        }
    });

    form.addEventListener('change', function (e) {
        const field = e.target;
        if (field.tagName === 'SELECT' && field.value) {
            clearFieldError(field);
        }
        if (field.tagName === 'INPUT' && field.value.trim()) {
            clearFieldError(field);
        }
    });

    form.addEventListener('changeDate', function (e) {
        const field = e.target;
        if (field && field.value) {
            clearFieldError(field);
        }
    });

    const datepickerInputs = form.querySelectorAll('[datepicker]');
    datepickerInputs.forEach(function (input) {
        const observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                    if (input.value) {
                        clearFieldError(input);
                    }
                }
            });
        });
        observer.observe(input, { attributes: true, attributeFilter: ['value'] });

        input.addEventListener('blur', function () {
            if (this.value) {
                clearFieldError(this);
            }
        });
    });
}

// ========================================================
// USER-EDIT TRACKING
// ========================================================

function createEditTracker() {
    const userEditedFields = new Set();
    let lastAutoFilledSlotDuration = null;

    return {
        mark(fieldName) { userEditedFields.add(fieldName); },
        isEdited(fieldName) { return userEditedFields.has(fieldName); },
        reset(fieldName) { userEditedFields.delete(fieldName); },
        get lastSlotDuration() { return lastAutoFilledSlotDuration; },
        set lastSlotDuration(val) { lastAutoFilledSlotDuration = val; },
    };
}

// ========================================================
// SERVICE TYPE DEFAULTS
// ========================================================

async function fetchServiceTypeDefaults(_serviceTypeId) {
    // PR-6c-2 (2026-05) — endpoint
    // ``/office/api/service-types/<id>/defaults/`` was removed
    // together with the ``ServiceType`` model. Returning ``null``
    // keeps every downstream consumer (``createServiceTypeHandler``,
    // ``initJobFormPage``) on its no-op branch. PR-6c-4 (next) will
    // restore a fetch against the new
    // ``ContractCategory``-backed endpoint.
    return null;
}

function createServiceTypeHandler(tracker, elements, onApplied) {
    const { durationInput, fixedPriceInput, hourlyRateInput, pricingModelInput } = elements;

    async function applyDefaults(serviceTypeIdOverride) {
        const serviceTypeId = serviceTypeIdOverride || document.querySelector('[name="service_type"]')?.value;
        if (!serviceTypeId) return;

        const defaults = await fetchServiceTypeDefaults(serviceTypeId);
        if (!defaults) return;

        tracker.reset('duration');
        tracker.reset('fixed_price');
        tracker.reset('hourly_rate');

        if (!tracker.isEdited('duration') && durationInput && defaults.default_duration_minutes) {
            durationInput.value = defaults.default_duration_minutes;
        }

        if (defaults.default_duration_minutes) {
            document.querySelectorAll('input[name$="duration_minutes"]').forEach(input => {
                if (input.name === 'default_duration_minutes') return;
                const currentValue = input.value;
                const isEmpty = !currentValue || currentValue === '';
                const wasAutoFilled = isEmpty || currentValue === '120' || currentValue == tracker.lastSlotDuration;
                if (wasAutoFilled) {
                    input.value = defaults.default_duration_minutes;
                }
            });
            tracker.lastSlotDuration = defaults.default_duration_minutes;
        }

        const hasFixed = defaults.has_fixed_price;
        const hasHourly = defaults.has_hourly_rate;

        if (pricingModelInput) {
            if (hasFixed && !hasHourly) pricingModelInput.value = 'fixed';
            else if (hasHourly && !hasFixed) pricingModelInput.value = 'hourly';
            else if (hasFixed && hasHourly) pricingModelInput.value = 'fixed';
        }

        const currentPricingModel = pricingModelInput ? pricingModelInput.value : 'fixed';

        if (currentPricingModel === 'fixed') {
            if (fixedPriceInput) {
                const dp = _parseMoney(defaults.default_price);
                fixedPriceInput.value = Number.isFinite(dp) ? _formatMoney(dp) : '';
            }
            if (hourlyRateInput) hourlyRateInput.value = '';
        } else if (currentPricingModel === 'hourly') {
            if (hourlyRateInput) {
                const hr = _parseMoney(defaults.default_hourly_rate);
                hourlyRateInput.value = Number.isFinite(hr) ? _formatMoney(hr) : '';
            }
            if (fixedPriceInput) fixedPriceInput.value = '';
        }

        updatePricingFields(elements);

        if (onApplied) onApplied(defaults);
    }

    return applyDefaults;
}

// ========================================================
// LOCATION FILTERING
// ========================================================

function updateLocations(customerHiddenInput, locations) {
    if (!customerHiddenInput) return;
    const customerId = customerHiddenInput.value;
    const locSelect = document.getElementById('id_location');
    const currentLocation = locSelect ? locSelect.value : '';

    if (!locSelect) return;
    locSelect.innerHTML = '';

    if (!customerId) {
        locSelect.disabled = true;
        return;
    }

    locSelect.disabled = false;

    if (locations) {
        locations.forEach(loc => {
            if (String(loc.customer_id) === String(customerId)) {
                const opt = document.createElement('option');
                opt.value = loc.id;
                opt.textContent = loc.label;
                if (String(loc.id) === String(currentLocation)) opt.selected = true;
                locSelect.appendChild(opt);
            }
        });
    }

    // Picking a customer auto-populates the address (a single-location
    // customer implicitly selects its only option). That's a programmatic
    // value change — it does NOT fire a native ``change`` event — so the
    // form-level validation listener never clears a server-rendered
    // « This field is required » error. Clear it here once the field
    // actually holds a value.
    if (locSelect.value) {
        clearFieldError(locSelect);
    }
}

// PR3 Slice C — Worksite picker. Mirrors ``updateLocations``: when
// the customer dropdown changes, prune the worksite ``<select>`` to
// only the worksites belonging to the new customer. Server-side the
// queryset is org-scoped + the ``clean()`` method rejects any
// cross-customer worksite injected via a tampered POST, so the JS
// is purely a UX nicety (don't make the user scan worksites of
// other customers in the dropdown).
// Worksite Lifecycle (May 2026, PO Marie Laurent third-round
// follow-up) — picker visibility filter.
//
// The cache ``worksitesJson`` may now contain worksites that were
// LIVE-AVAILABLE at GET-render time but have since become
// unavailable due to a parallel-tab edit (archived =
// ``is_active=false``, or picked up by tab 2 = ``has_live_job=true``).
// The bulk live-fetch endpoint patches those flags in place, and
// this helper hides any worksite that the bulk refresh marked as
// unavailable from the picker.
//
// We intentionally do NOT remove the entries from the cache itself:
// keeping them lets us RE-show them when (a) tab 2 unarchives the
// worksite (``is_active`` flips back to true), or (b) tab 2 cancels
// the live job (``has_live_job`` flips back to false). The cache
// stays the source of truth; the picker is the projection.
//
// Defensive defaults: a worksite that pre-dates the bulk-refresh
// hook (no ``is_active`` / ``has_live_job`` keys at all) is treated
// as available — the JSON serializer guarantees them on every
// fresh render, but a legacy cached entry must not disappear from
// the picker just because its keys are missing.
function _isWorksiteAvailableForPicker(ws) {
    if (!ws) return false;
    if (ws.is_active === false) return false;
    if (ws.has_live_job === true) return false;
    return true;
}

function updateWorkSites(customerHiddenInput, worksites) {
    if (!customerHiddenInput) return;
    const customerId = customerHiddenInput.value;
    const wsSelect = document.getElementById('id_work_site');
    if (!wsSelect) return;
    const currentWs = wsSelect.value;

    // Preserve the empty option (rendered server-side as "No worksite ...")
    // — clearing innerHTML wipes it, so we capture and re-insert.
    const emptyOpt = wsSelect.querySelector('option[value=""]');
    wsSelect.innerHTML = '';
    if (emptyOpt) wsSelect.appendChild(emptyOpt);

    if (!customerId) {
        wsSelect.disabled = true;
        renderWorkSiteEmptyHint(null, 0);
        return;
    }
    wsSelect.disabled = false;

    let attached = 0;
    let currentWsStillAvailable = !currentWs;
    if (worksites) {
        worksites.forEach(ws => {
            if (String(ws.customer_id) !== String(customerId)) return;
            if (!_isWorksiteAvailableForPicker(ws)) {
                if (String(ws.id) === String(currentWs)) {
                    // Track that the previously-selected worksite is
                    // gone from the picker so the caller can clear
                    // the selection + re-run verrous downstream.
                    currentWsStillAvailable = false;
                }
                return;
            }
            const opt = document.createElement('option');
            opt.value = ws.id;
            opt.textContent = ws.label;
            if (String(ws.id) === String(currentWs)) {
                opt.selected = true;
                currentWsStillAvailable = true;
            }
            wsSelect.appendChild(opt);
            attached += 1;
        });
    }

    renderWorkSiteEmptyHint(customerId, attached);

    // Worksite Lifecycle (May 2026, third-round) — if the worksite
    // the office had selected is no longer in the picker (archived
    // / picked-up-by-tab-2), clear the selection and fire a synthetic
    // ``change`` event so every downstream handler (Location lock,
    // date override, verrou, snapshot sync) settles into the
    // worksite-empty state. Other typed-in fields stay intact —
    // the office sees the picker emptied, can pick a different
    // worksite, and resume without losing their work.
    if (!currentWsStillAvailable && currentWs) {
        wsSelect.value = '';
        wsSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
}

// Slice A — *visits-without-mobile-app* verrou CIaO (§ 6.4 layer 1
// / 3). Lock the ``mobile_tracking_disabled`` checkbox to "False +
// disabled" whenever the selected ``work_site`` × ``service_type``
// combination puts the Job in CIaO scope. Mirrors exactly the
// server-side predicate ``Job.is_in_ciao_scope`` (which mirrors the
// model-level helper ``_work_site_status_in_ciao_scope`` plus the
// service-type movability check). The two payload booleans
// ``ws.in_ciao_scope`` and ``st.is_movable_property`` are
// precomputed in ``apps/office/views_create.py::_get_form_context_data``
// so the JS doesn't re-derive the rule client-side.
//
// A disabled checkbox is omitted from FormData on submit, so when
// the verrou kicks in the server reads ``mobile_tracking_disabled``
// as False — exactly the desired behaviour. The form-layer
// ``clean()`` (Slice A.2) and the model-layer ``Job.clean()`` (Slice
// A.1) are the defence-in-depth gates if this layer is bypassed
// (no JS, hostile POST, admin/shell path).
function _resolveTrackingMode(worksites, _serviceTypes) {
    // Returns one of three modes the JS verrou can be in, mirroring
    // the server-side predicate ``Job.is_in_ciao_scope`` after the
    // PR-6c-2 (2026-05) drop of the ``ServiceType`` model:
    //
    //   work_site IS NOT NULL
    //     AND ws.in_ciao_scope (status + DDT + ws.is_movable_property
    //                           — all rolled up server-side)
    //   THEN
    //     ws.uses_own_system_only      → 'externally-handled'
    //     ELSE                         → 'mobile-required'
    //   ELSE                           → 'free' (office picks freely)
    //
    // Pre-PR-6c-2, this helper also AND'd ``NOT service_type.
    // is_movable_property``. With ``ServiceType`` dropped, the
    // movable-property semantics moved entirely to ``WorkSite.
    // is_movable_property`` (PR-6a) and are folded into
    // ``ws.in_ciao_scope`` by the server-side builder
    // (``apps/office/views_create.py::_get_form_context_data``), so
    // the JS only needs the worksite-level boolean.
    //
    // The ``_serviceTypes`` parameter is kept for call-site stability
    // (every caller still threads it through); PR-6c-4 will repurpose
    // it for the ``ContractCategory`` cache without churning the
    // signatures again.
    const wsSelect = document.getElementById('id_work_site');
    const wsId = wsSelect ? wsSelect.value : '';

    const ws = wsId
        ? (worksites || []).find(w => String(w.id) === String(wsId))
        : null;

    const inCiaoScope = !!(ws && ws.in_ciao_scope);
    if (!inCiaoScope) {
        return { mode: 'free', ws: null };
    }
    if (ws.uses_own_system_only === true) {
        return { mode: 'externally-handled', ws };
    }
    return { mode: 'mobile-required', ws };
}

function updateMobileTrackingState(worksites, serviceTypes, translations) {
    // Slices A § 6.4 + F § 7.4 — JS layer 1 / 3 of the CIaO verrou
    // on the ``mobile_tracking_disabled`` checkbox.
    //
    // Pre-F implementation cached the server's ``disabled`` verdict
    // on a per-element flag (``_mobileTrackingServerLocked``) and
    // refused to ever re-evaluate, because the original verrou only
    // had ONE direction (lock-OFF) and the JS could not tell whether
    // a server-disabled checkbox came from CIaO scope or from the
    // checkbox just being read-only on a frozen field. With Slice F
    // introducing a SECOND lock direction (lock-ON when the worksite
    // is 100% USES_OWN_SYSTEM), we now compute both directions
    // client-side from the same ``worksites`` payload the server
    // uses (Slice F.4 added ``ws.uses_own_system_only``), so the
    // server-cache trick is unnecessary. The result MATCHES the
    // server's verdict on the initial render, and continues to
    // re-evaluate live as the office mutates the picker on the
    // create form (the edit form keeps ``work_site`` immutable, so
    // the mode never changes mid-flow there).
    const checkbox = document.getElementById('id_mobile_tracking_disabled');
    const helpEl = document.getElementById('mobile-tracking-help');
    if (!checkbox) return;

    const { mode, ws } = _resolveTrackingMode(worksites, serviceTypes);
    const wsLabel = (ws && ws.label) || '';
    const t = translations || {};

    // PO bug 2026-05-15 (Marie Laurent 11:55) — selecting an
    // externally-handled worksite force-checks the box (verrou)
    // and hides the Checklist card. If the office then **deselects**
    // the worksite ("aucun chantier") the verrou releases and the
    // ``free`` branch below un-disables the checkbox — but the
    // ``checked`` state was never reset, so the box stayed ticked
    // and the Checklist card stayed hidden. The office had no way
    // to recover other than manually un-ticking the box.
    //
    // Fix: snapshot the user's pre-verrou ``checked`` state the
    // first time the verrou kicks in (whether it forces ON or OFF),
    // then restore it when the verrou releases. Free → free
    // transitions are no-ops (the snapshot is only taken when
    // entering a locked mode, and only consumed when leaving one).
    //
    // Examples (all from the office's perspective):
    //   • free+unchecked → externally-handled: snapshot=false, force ON.
    //     Then back to free: restore false → unchecked. Card re-appears.
    //   • free+checked   → externally-handled: snapshot=true,  force ON.
    //     Then back to free: restore true → checked. Card stays hidden.
    //     (The office's prior office-only opt-in is honoured.)
    //   • free+checked   → mobile-required:    snapshot=true,  force OFF.
    //     Then back to free: restore true → checked. Card stays hidden.
    //   • free+unchecked → mobile-required:    snapshot=false, force OFF.
    //     Then back to free: restore false → unchecked. Card stays visible.
    const wasLocked = !!checkbox._mtdVerrouActive;
    const isLocked = mode === 'externally-handled' || mode === 'mobile-required';

    if (isLocked && !wasLocked) {
        // Entering a locked mode — capture the office's current
        // choice so we can restore it when the verrou releases.
        checkbox._mtdPreVerrouChecked = checkbox.checked;
        checkbox._mtdVerrouActive = true;
    }

    if (mode === 'externally-handled') {
        checkbox.checked = true;
        checkbox.disabled = true;
        _syncMobileTrackingHiddenMirror(checkbox, true);
        if (helpEl) {
            const tpl = t.mobileTrackingExternallyHandledHelp || '';
            helpEl.textContent = tpl.replace('{ws}', wsLabel);
        }
    } else if (mode === 'mobile-required') {
        checkbox.checked = false;
        checkbox.disabled = true;
        _syncMobileTrackingHiddenMirror(checkbox, false);
        if (helpEl) {
            const tpl = t.mobileTrackingCiaoLockHelp || '';
            helpEl.textContent = tpl.replace('{ws}', wsLabel);
        }
    } else {
        if (wasLocked) {
            // Leaving a locked mode — restore the snapshot taken
            // on entry so the office's prior choice survives the
            // round-trip through the verrou.
            checkbox.checked = !!checkbox._mtdPreVerrouChecked;
            checkbox._mtdVerrouActive = false;
            delete checkbox._mtdPreVerrouChecked;
        }
        checkbox.disabled = false;
        _syncMobileTrackingHiddenMirror(checkbox, null);
        if (helpEl) {
            helpEl.textContent = t.mobileTrackingDefaultHelp || '';
        }
    }

    // Slice F.22 (PO request 2026-05-15) — without mobile tracking
    // the cleaner never opens the app, so the checklist template
    // is dead data (no UI consumes it on Mark Done office-only and
    // externally-handled visits don't reach our app at all). Hide
    // the entire card so the office isn't asked to maintain a list
    // that no surface will read. Symmetric with Slice F.19 which
    // hides the "Work Report" card on the visit drawer for the
    // exact same set of visits. We read ``checkbox.checked``
    // (rather than ``mode``) so the toggle also reacts to the
    // office hand-ticking the box in the 'free' mode without a
    // worksite.
    _syncChecklistCardVisibility(checkbox.checked);
}

// Slice F.22 follow-up — toggle the Checklist card on the job
// form according to the live ``mobile_tracking_disabled`` state.
// Called from ``updateMobileTrackingState`` (worksite / service-
// type / verrou changes) AND from a direct ``change`` listener on
// the checkbox itself (free-mode hand-toggle, see
// ``initJobFormPage``). Idempotent: setting the same class twice
// is a no-op so the helper stays cheap on every re-eval.
function _syncChecklistCardVisibility(mobileTrackingDisabled) {
    const wrapper = document.getElementById('checklist-card-wrapper');
    if (!wrapper) return;
    wrapper.classList.toggle('hidden', !!mobileTrackingDisabled);
}

// PO bug 2026-05-15 (Marie Laurent screenshot 10:55) — when
// ``updateMobileTrackingState`` HTML-disables the
// ``mobile_tracking_disabled`` checkbox, the browser stops including
// it in the form POST body (this is the HTML spec for disabled form
// controls — a checked-but-disabled checkbox sends nothing). On the
// CREATE form the server then reads ``mobile_tracking_disabled =
// False`` and rejects the submission with ``"Office-only tracking is
// required on this Job…"`` even though the verrou had auto-checked
// the box visually. The EDIT form is immune because Django's
// ``field.disabled = True`` (a Python form-field flag, not the HTML
// attribute) substitutes the missing POST value with the field's
// ``initial``; the create JS sets only the HTML attribute.
//
// Fix: maintain a hidden ``<input type="hidden" name="mobile_
// tracking_disabled">`` mirror right after the checkbox whenever the
// checkbox is HTML-disabled. Two siblings share the same name; the
// browser ignores the disabled one and only the hidden mirror is
// sent. When the checkbox is re-enabled (verrou released), tear the
// mirror down so the live checkbox state is what the server sees.
//
// ``lockedValue`` semantics:
//   true  → externally-handled lock (mtd MUST be True server-side)
//   false → mobile-required lock     (mtd MUST be False server-side)
//   null  → no lock; remove the mirror, the live checkbox carries
//           its own POST value as usual.
//
// Django CheckboxInput parses any non-empty string as ``True`` (and
// the absence of the field as ``False``), so we use the literal
// ``"on"`` for True (matches the native browser submission for a
// checked checkbox) and skip the mirror entirely for False (the
// server already defaults to False on absence).
function _syncMobileTrackingHiddenMirror(checkbox, lockedValue) {
    if (!checkbox) return;
    const MIRROR_ID = 'id_mobile_tracking_disabled__mirror';
    const existing = document.getElementById(MIRROR_ID);

    if (lockedValue === null) {
        if (existing) existing.remove();
        return;
    }

    if (lockedValue === false) {
        // No mirror needed: an absent field is parsed as False by
        // Django's BooleanField. Removing any pre-existing mirror
        // makes sure a previous "lock-ON" state isn't sticky.
        if (existing) existing.remove();
        return;
    }

    if (existing) {
        existing.value = 'on';
        return;
    }

    const mirror = document.createElement('input');
    mirror.type = 'hidden';
    mirror.id = MIRROR_ID;
    mirror.name = checkbox.name || 'mobile_tracking_disabled';
    mirror.value = 'on';
    checkbox.parentNode.insertBefore(mirror, checkbox.nextSibling);
}

// Slice F.23 — *optimistic concurrency check* for the CIaO-relevant
// flags of the selected service_type / work_site (PO bug 2026-05-15
// — Marie Laurent: race condition between two tabs).
//
// The Job's CIaO classification (``Job.is_in_ciao_scope``) is derived
// from the LIVE values of ``ServiceType.is_movable_property`` and the
// ``WorkSite`` status at the moment ``JobForm.clean()`` runs. If
// either flag flips in a parallel tab between GET-render and POST,
// the Job is silently classified differently from what the office
// user saw on screen.
//
// Defence pattern (industry-standard "form version check"): two
// hidden inputs on the form embed the SNAPSHOT of those flags as
// they were rendered in the dropdown the office picked from. The
// matching server-side check (``apps/office/forms_create.py::
// _check_form_snapshot_concurrency``) rejects the form when the
// snapshot diverges from the live DB value.
//
// This helper keeps the two hidden inputs in sync with the current
// selection. It reads the data the server already pre-baked in
// ``serviceTypesJson`` / ``worksites`` (``data.serviceTypes`` /
// ``data.worksites`` in the page-level config), so the snapshot
// always reflects WHAT THE OFFICE SAW at GET-render time, even if
// the live DB has since drifted.
//
// Best-effort: if either select is missing, nothing happens. If the
// selection is empty, the matching hidden input is cleared (the
// server-side check skips empty snapshots and falls through to the
// existing live-DB-based logic).
// Worksite Lifecycle (May 2026, PO Marie Laurent UX follow-up to
// Slice F.23) — live re-fetch of the CIaO-relevant flags on every
// picker re-selection, so the form auto-recovers from cross-tab
// edits without needing a full page reload (which would wipe the
// office's typed-in data).
//
// Why this is needed: the verrou logic (greyed-out movable services,
// office-only checkbox, externally-handled banner) reads from the
// pre-baked ``worksitesJson`` / ``serviceTypesJson`` cache that the
// server inlined at GET-render time. When a parallel tab modifies
// the worksite's CIaO scope (DDT attached/detached, contract value
// crossing the 30 k€ threshold) or the service type's
// ``is_movable_property`` flag, the cache becomes stale: the office
// sees the verrou frozen on the old state, AND the Slice F.23
// snapshot-check rejects the submission at POST time.
//
// These two helpers issue a single GET to the matching live-fetch
// endpoint (``apps/office/views_create.py::get_worksite_ciao_scope``
// / ``get_service_type_movable_flag``), patch the cached entry in
// place, and let the caller re-run whatever verrou / snapshot
// helpers depend on the cache.
//
// Failure modes are ALWAYS silent: a network error / 404 leaves the
// cached value intact and the existing Slice F.23 snapshot check at
// POST time stays authoritative. This is a UX nicety, never a
// security control.
function _patchWorksiteCacheEntry(worksites, fresh) {
    if (!Array.isArray(worksites) || !fresh || fresh.id == null) return;
    const idx = worksites.findIndex(w => String(w.id) === String(fresh.id));
    if (idx < 0) return;
    if (typeof fresh.in_ciao_scope === 'boolean') {
        worksites[idx].in_ciao_scope = fresh.in_ciao_scope;
    }
    if (typeof fresh.status === 'string') {
        worksites[idx].status = fresh.status;
    }
    if (typeof fresh.is_active === 'boolean') {
        worksites[idx].is_active = fresh.is_active;
    }
}

function _patchServiceTypeCacheEntry(_serviceTypes, _fresh) {
    // PR-6c-2 (2026-05) — no-op after the ``ServiceType`` model drop.
    // Kept as a stub for source stability ; PR-6c-4 will repurpose it
    // for the ``ContractCategory`` cache.
}

async function refreshWorksiteCiaoScope(worksites, wsId, onUpdated) {
    if (!wsId) return;
    try {
        const response = await fetch(
            `/office/api/worksites/${encodeURIComponent(wsId)}/ciao-scope/`,
            { credentials: 'same-origin' },
        );
        if (!response.ok) return;
        const fresh = await response.json();
        _patchWorksiteCacheEntry(worksites, fresh);
    } catch (_e) {
        // Silent fail — cached value remains, POST-time snapshot
        // check is the authoritative defence.
        return;
    }
    if (typeof onUpdated === 'function') {
        try { onUpdated(); } catch (_e) { /* noop */ }
    }
}

// Bulk variant for the worksites cache — patches every cached entry
// in one round-trip + adds newly-available ones (e.g. a worksite
// that tab 2 unarchived since GET-render). Mirror of
// ``refreshAllServiceTypesMovableFlags`` for worksites; see the
// view docstring (``apps/office/views_create.py::
// get_worksites_availability``) for the full rationale.
//
// Patch policy: for each entry in the response,
//   * if a matching cache entry exists → update its mutable flags
//     in place (``is_active``, ``has_live_job``, ``in_ciao_scope``,
//     ``status``) so any other code reading from the cache sees the
//     fresh state. Other fields (``label``, ``customer_id``,
//     ``primary_location_id``, ``contract_*``, plus
//     ``declared_subcontractor_ids`` populated by the server-side
//     ``_get_form_context_data`` payload) are KEPT to preserve the
//     enrichment that this endpoint intentionally does not redo;
//   * if no matching cache entry exists → push the fresh entry as
//     a new row (limited to the fields the bulk endpoint surfaces).
//
// ``onUpdated`` runs even on network failure so the caller's
// ``updateWorkSites`` re-render is not skipped — failing to refresh
// the cache should never freeze the picker on its old state.
async function refreshAllWorksitesAvailability(worksites, onUpdated) {
    if (!Array.isArray(worksites)) return;
    let list = null;
    try {
        const response = await fetch(
            '/office/api/worksites/availability/',
            { credentials: 'same-origin' },
        );
        if (response.ok) {
            const body = await response.json();
            list = (body && body.worksites) || [];
        }
    } catch (_e) {
        list = null;
    }
    if (Array.isArray(list)) {
        list.forEach(fresh => {
            const idx = worksites.findIndex(
                w => String(w.id) === String(fresh.id),
            );
            if (idx >= 0) {
                worksites[idx].is_active = !!fresh.is_active;
                worksites[idx].has_live_job = !!fresh.has_live_job;
                worksites[idx].in_ciao_scope = !!fresh.in_ciao_scope;
                if (typeof fresh.status === 'string') {
                    worksites[idx].status = fresh.status;
                }
            } else {
                worksites.push(fresh);
            }
        });
    }
    if (typeof onUpdated === 'function') {
        try { onUpdated(); } catch (_e) { /* noop */ }
    }
}

async function refreshServiceTypeMovableFlag(_serviceTypes, _stId, onUpdated) {
    // PR-6c-2 (2026-05) — endpoint
    // ``/office/api/service-types/<id>/movable-flag/`` removed
    // together with the ``ServiceType`` model. ``onUpdated`` still
    // fires so any caller's post-refresh re-render is not silently
    // skipped (kept symmetric with the worksite variant). PR-6c-4
    // will repurpose this for ``ContractCategory``.
    if (typeof onUpdated === 'function') {
        try { onUpdated(); } catch (_e) { /* noop */ }
    }
}

// Bulk variant of ``refreshServiceTypeMovableFlag`` — patches the
// entire ``serviceTypesJson`` cache from a single round-trip so the
// service-type picker verrou re-evaluates ALL options against fresh
// data, not only the selected row.
//
// PO Marie Laurent reported the underlying bug on 2026-05-15
// (follow-up to the first cross-tab live-sync round): unticking
// ``is_movable_property`` on a service type in tab 2 left tab 1's
// greyed-out row in the picker FROZEN, because the per-id endpoint
// only refreshed the SELECTED service type — and the office could
// not select a greyed-out option to trigger that refresh in the
// first place. Chicken-and-egg: the bulk endpoint cuts the loop.
//
// Wired to BOTH the worksite-change and service-type-change events
// so any user interaction with the verrou's inputs triggers a
// full-cache refresh. ~one round-trip cost per picker click —
// payload is small (one row per service type, only 3 boolean-ish
// fields), and the org's service-type catalog is bounded.
async function refreshAllServiceTypesMovableFlags(_serviceTypes, onUpdated) {
    // PR-6c-2 (2026-05) — endpoint
    // ``/office/api/service-types/movable-flags/`` removed together
    // with the ``ServiceType`` model. ``onUpdated`` still fires so
    // callers' post-refresh re-renders are not silently skipped.
    // PR-6c-4 will repurpose this for ``ContractCategory``.
    if (typeof onUpdated === 'function') {
        try { onUpdated(); } catch (_e) { /* noop */ }
    }
}

function _syncCiaoSnapshotFields(_serviceTypes, worksites) {
    // PR-6c-2 (2026-05) — the ``id_ciao_snapshot_st_movable`` hidden
    // field was removed together with the ``ServiceType`` model. The
    // matching server-side optimistic concurrency check now reads
    // only ``ciao_snapshot_ws_in_scope``. The ``_serviceTypes``
    // parameter is kept for call-site stability ; PR-6c-4 will reuse
    // it for the ``ContractCategory`` snapshot.

    const wsHidden = document.getElementById('id_ciao_snapshot_ws_in_scope');
    if (wsHidden) {
        const wsSelect = document.getElementById('id_work_site');
        const wsId = wsSelect ? wsSelect.value : '';
        if (wsId) {
            const ws = (worksites || []).find(
                w => String(w.id) === String(wsId),
            );
            if (ws) {
                wsHidden.value = ws.in_ciao_scope ? 'true' : 'false';
            } else {
                wsHidden.value = '';
            }
        } else {
            wsHidden.value = '';
        }
    }
}

// Slice F.20 (PO bug 2026-05-15) — service-type picker: grey out
// every ``is_movable_property=true`` option when the selected
// worksite is in CIaO scope (``ws.in_ciao_scope === true``).
//
// Rationale: a movable-property service type (vehicles, planes,
// trains, trucks per ONSS FAQ Q14-Q15) bypasses the CIaO regime
// regardless of the worksite. Combining a CIaO worksite with a
// movable service is therefore mutually exclusive — the office
// almost certainly mis-picked one of the two. Mirroring the
// existing on-hold customer pattern (greyed-out options in the
// customer picker), we visually mark the incompatibility right
// in the service-type dropdown so the office cannot silently
// produce the contradiction.
//
// The server is intentionally NOT a hard gate on this combination:
// ``Job.is_in_ciao_scope`` reads ``False`` when the service type
// is movable, so a tampered POST simply produces a non-CIaO Job
// (no validation error). The JS layer is purely UX prevention.
//
// Implementation note — operates on the native ``<option>``
// elements of the ``<select name="service_type">`` rendered by
// Django's default widget. The original option label is cached
// in ``data-original-label`` on the first sweep so repeated
// re-evaluations (worksite swap, then swap back) restore it
// cleanly without accumulating suffixes.
function updateServiceTypePickerLockState(_worksites, _serviceTypes, _translations) {
    // PR-6c-2 (2026-05) — the ``<select name="service_type">``
    // picker was removed together with the ``ServiceType`` model,
    // so this helper has no DOM target. Kept as a stub so the call
    // sites (initJobFormPage, the worksite-change handler, ...)
    // don't need branching ; PR-6c-4 will reinstate the lock for
    // the new ``ContractCategory`` picker once it's introduced.
    //
    // The original implementation (pre-PR-6c-2) :
    //   - cached the pristine option label in ``data-original-label``
    //   - disabled every option flagged
    //     ``service_type.is_movable_property`` when the selected
    //     worksite was in CIaO scope
    //   - cleared the selection if the now-disabled row was the
    //     active one and bound a one-shot mousedown guard so
    //     tampered POSTs / themed pickers couldn't slip a disabled
    //     value past the native gate.
    //
    // That logic is preserved in git history (commit pin in
    // ``IMPLEMENTATION_PLAN.md § PR-6c-2``) and will be replicated
    // for the ``ContractCategory`` picker in PR-6c-4.
}

function updateCleanerPickerLockState(worksites, serviceTypes, translations) {
    // Slice F § 7.5 — JS layer 1 / 3 of the cleaner-assignment block
    // mirrored from ``Job.clean()`` and the ``m2m_changed`` signals
    // (Slice F.2). When the resolved mode is 'externally-handled',
    // every CleanTask cleaner has to be removed from the selection
    // and the picker frozen so the office cannot add a new one.
    // Re-runs on every worksite / service-type change — the office
    // can recover by switching back to a non-externally-handled
    // worksite (the picker re-enables and any pre-existing chips
    // would have been wiped, which is the intentional safe
    // direction).
    const { mode, ws } = _resolveTrackingMode(worksites, serviceTypes);
    const wsLabel = (ws && ws.label) || '';
    const t = translations || {};
    const externallyHandled = mode === 'externally-handled';

    const pickerEl = document.getElementById('job-cleaner-picker');
    if (pickerEl) {
        const addBtn = document.getElementById('job-add-cleaner-btn');
        const clearBtn = document.getElementById('job-clear-all-btn');
        const dropdown = document.getElementById('job-cleaner-dropdown');
        const chipsContainer = document.getElementById('job-selected-chips');
        const hiddenSelect = document.getElementById('job-assigned-employees-select');
        const lockMsgEl = document.getElementById('cleaner-picker-lock-msg');

        if (externallyHandled) {
            if (addBtn) addBtn.setAttribute('disabled', 'disabled');
            if (clearBtn) clearBtn.setAttribute('disabled', 'disabled');
            if (dropdown) dropdown.classList.add('hidden');
            if (chipsContainer) {
                // Wipe any existing chips — the office can re-add
                // cleaners after switching back to a non-externally-
                // handled worksite. The hidden ``<select>`` mirror is
                // wiped in the same pass so a stray POST cannot smuggle
                // an assignment past the form's ``clean()`` guard.
                chipsContainer.innerHTML = '';
            }
            if (hiddenSelect) {
                Array.from(hiddenSelect.options).forEach(opt => {
                    opt.selected = false;
                });
            }
            if (lockMsgEl) {
                const tpl = t.cleanerPickerExternallyHandledHelp || '';
                lockMsgEl.textContent = tpl.replace('{ws}', wsLabel);
                lockMsgEl.classList.remove('hidden');
            }
            pickerEl.classList.add('opacity-50', 'pointer-events-none');
        } else {
            if (addBtn) addBtn.removeAttribute('disabled');
            if (clearBtn) clearBtn.removeAttribute('disabled');
            if (lockMsgEl) {
                lockMsgEl.classList.add('hidden');
                lockMsgEl.textContent = '';
            }
            pickerEl.classList.remove('opacity-50', 'pointer-events-none');
        }
    }

    // PR-D (2026-06) — the per-slot pickers were removed with the
    // JobSlot model (migration 0098). Only the job-level picker above
    // needs the externally-handled lockdown now.
}

// Companion to ``updateWorkSites``. The Job form's worksite picker is
// a 1:1 binding to a Worksite row that is NOT yet attached to a Job
// (model constraint ``one_job_per_worksite``). When the selected
// customer has zero such worksites, the dropdown collapses to just
// "No worksite", and the office has no in-context cue that they need
// to *create* a worksite first. We render a small helper text under
// the picker — only on the create form, only when ``customerId`` is
// known and ``count == 0`` — pointing at the canonical
// ``office:worksite_create`` URL with the customer pre-bound. The
// edit form skips this entirely (the worksite is immutable on edit
// and the bound option is rendered server-side).
function renderWorkSiteEmptyHint(customerId, count) {
    const wsSelect = document.getElementById('id_work_site');
    if (!wsSelect) return;
    const formEl = document.getElementById('job-form');
    const isEdit = formEl && formEl.dataset.isEdit === 'true';
    if (isEdit) return;

    const HINT_ID = 'work-site-empty-hint';
    let hint = document.getElementById(HINT_ID);

    if (!customerId || count > 0) {
        if (hint) hint.remove();
        return;
    }

    const urlTemplate = formEl
        ? formEl.dataset.worksiteCreateUrlTemplate
        : null;
    if (!urlTemplate) {
        // No template available — bail silently rather than render a
        // dead link. The pre-existing UX (empty dropdown) still works,
        // we just don't enrich it.
        if (hint) hint.remove();
        return;
    }
    const url = urlTemplate.replace('/0/', `/${encodeURIComponent(customerId)}/`);
    const label = wsSelect.dataset.emptyHintLabel
        || 'No worksite available for this customer.';
    const linkLabel = wsSelect.dataset.emptyHintLink
        || 'Create one';

    if (!hint) {
        hint = document.createElement('p');
        hint.id = HINT_ID;
        hint.className = 'mt-1 text-xs text-amber-600 dark:text-amber-400';
        // The native ``<select>`` is wrapped at runtime by
        // ``custom-select.js`` (``.custom-select-wrapper`` containing
        // a hidden select + a visible button). Inserting ``afterend``
        // of the native select lands the hint *inside* that wrapper
        // and breaks the visual flow. Append to the closest
        // ``.field-group`` (the form-row container) so the hint
        // always renders as the last child, directly under the
        // visible select button. Fall back to the wrapper's
        // ``afterend`` (then the bare select's ``afterend``) if the
        // template ever drops the field-group convention.
        const fieldGroup = wsSelect.closest('.field-group');
        const wrapper = wsSelect.closest('.custom-select-wrapper') || wsSelect;
        if (fieldGroup) {
            fieldGroup.appendChild(hint);
        } else {
            wrapper.insertAdjacentElement('afterend', hint);
        }
    }
    hint.innerHTML = '';
    hint.appendChild(document.createTextNode(`${label} `));
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    link.className = 'underline hover:text-amber-800 dark:hover:text-amber-300';
    link.textContent = linkLabel;
    hint.appendChild(link);
}

// PR3 §5.7 — when a Worksite is picked, the address (Location) of
// the visit is no longer a free choice: the Worksite carries its
// own ``primary_location`` and that's the address the DDT was
// filed against. Letting the office change Location independently
// would silently desync the visit from the ONSS-registered scope,
// which is exactly the bug the Worksite picker was added to
// prevent.
//
// IMPORTANT — interaction with ``custom-select.js``: the native
// ``<select id="id_location">`` is wrapped at boot time by a
// custom dropdown that *swallows* user clicks (the wrapper button
// takes over). Setting Tailwind classes on the native <select>
// has no visible effect; the wrapper's ``MutationObserver`` only
// watches ``childList`` + ``attributeFilter: ['disabled']``, so
// the right way to gray out the dropdown is ``select.disabled =
// true`` (the observer mirrors that onto the button, opacity 0.6,
// click no-op). Programmatic ``select.value = …`` doesn't fire a
// ``change`` event, so we dispatch one manually to push the new
// label onto the wrapper button.
//
// A plain ``disabled`` <select> drops out of FormData on submit —
// the required-validator would then bounce the form. We re-enable
// it just before submit (see the form ``submit`` handler in
// ``initJobForm``) so the value POSTs as expected. Server-side
// ``JobForm.clean()`` re-applies the same override — the JS lock
// is purely a UX hint, never the security boundary.
function applyWorkSiteLocationLock(worksites) {
    const wsSelect = document.getElementById('id_work_site');
    const locSelect = document.getElementById('id_location');
    if (!wsSelect || !locSelect) return;

    function unlock() {
        // Only re-enable if WE had locked it — ``updateLocations``
        // also disables the select when no customer is selected,
        // and we must not stomp on that state.
        if (locSelect.dataset.lockedByWorksite === 'true') {
            locSelect.disabled = false;
            delete locSelect.dataset.lockedByWorksite;
        }
    }

    const wsId = wsSelect.value;
    if (!wsId) {
        unlock();
        return;
    }

    const ws = worksites
        ? worksites.find(w => String(w.id) === String(wsId))
        : null;
    if (!ws || !ws.primary_location_id) {
        // Worksite carries no primary_location — leave Location
        // free so the office can still pick something. Edge case
        // for legacy worksites; the UI should later require a
        // primary_location at worksite creation.
        unlock();
        return;
    }

    // Sync Location to the worksite's primary location. The option
    // is guaranteed to be in the dropdown because ``updateLocations``
    // populates from the customer's locations and the worksite's
    // primary_location belongs to the same customer (enforced at the
    // worksite-create form level).
    const targetOpt = locSelect.querySelector(
        `option[value="${ws.primary_location_id}"]`,
    );
    if (targetOpt) {
        Array.from(locSelect.options).forEach(o => { o.selected = false; });
        targetOpt.selected = true;
        locSelect.value = String(ws.primary_location_id);
        // Notify the custom-select wrapper that the value changed
        // (programmatic value writes don't fire ``change``).
        locSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    locSelect.dataset.lockedByWorksite = 'true';
    locSelect.disabled = true;

    // Repaint any previous "field is required" error on Location
    // since the lock guarantees a value is now set.
    clearFieldError(locSelect);
}

// PR3 §5.9 — Worksite as initial date source-of-truth. When the
// office picks a Worksite, the Job's schedule dates
// (``one_off_date`` for one-off, ``start_date`` + ``end_date`` for
// weekly/biweekly) auto-fill from the Worksite's
// ``contract_start`` / ``contract_end``. The lower bound is
// clamped to today (a Job cannot start in the past). Monthly
// jobs have no date sync — they use ``monthly_day`` only.
//
// The office can then manually override the auto-filled values;
// on Job save the server overwrites the Worksite's contract dates
// with the Job's values (write-back lives in
// ``JobForm.save()`` / ``JobEditForm.save()``).
//
// Triggered exclusively on the worksite ``change`` event — never
// on initial render, so an existing Job's saved dates are
// preserved on the edit form (where the worksite picker is
// ``disabled=True`` anyway).
function formatDateForFlowbite(isoDate) {
    if (!isoDate) return '';
    const parts = String(isoDate).split('-');
    if (parts.length !== 3) return '';
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function clampToToday(isoDate) {
    if (!isoDate) return '';
    const today = new Date();
    const todayIso = today.toISOString().slice(0, 10);
    return isoDate < todayIso ? todayIso : isoDate;
}

function setDatepickerValue(input, dmyValue) {
    if (!input) return;
    const previous = input.value;
    if (previous === dmyValue) return;
    input.value = dmyValue;
    // Flowbite's Datepicker listens for ``changeDate`` to update
    // its internal state and the calendar selection; firing it
    // keeps the picker UI in sync with the programmatic write.
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new CustomEvent('changeDate', { bubbles: true }));
}

function applyWorkSiteDateOverride(worksites) {
    const wsSelect = document.getElementById('id_work_site');
    if (!wsSelect) return;
    const wsId = wsSelect.value;

    const recurrenceEl = document.getElementById('id_recurrence');
    const recurrence = recurrenceEl ? recurrenceEl.value : '';

    // "No worksite" selected → clear any dates that were
    // previously inherited from a worksite. The office's intent
    // is "I no longer have a compliance scope tied to this Job",
    // and a stale auto-filled date would silently survive into
    // the POST. Mirrors the symmetric Location-unlock behaviour
    // in ``applyWorkSiteLocationLock``. Monthly is still a no-op
    // (no schedule date to clear).
    if (!wsId) {
        if (recurrence === 'one_off') {
            setDatepickerValue(document.getElementById('id_one_off_date'), '');
        } else if (recurrence === 'weekly' || recurrence === 'biweekly') {
            setDatepickerValue(document.getElementById('id_start_date'), '');
            setDatepickerValue(document.getElementById('id_end_date'), '');
        }
        return;
    }

    const ws = worksites
        ? worksites.find(w => String(w.id) === String(wsId))
        : null;
    if (!ws) return;

    const startIso = clampToToday(ws.contract_start || '');
    const endIso = ws.contract_end || '';

    if (recurrence === 'one_off') {
        const oneOffInput = document.getElementById('id_one_off_date');
        setDatepickerValue(oneOffInput, formatDateForFlowbite(startIso));
    } else if (recurrence === 'weekly' || recurrence === 'biweekly') {
        const startInput = document.getElementById('id_start_date');
        const endInput = document.getElementById('id_end_date');
        setDatepickerValue(startInput, formatDateForFlowbite(startIso));
        // ``end_date`` is optional on the Job (and ``contract_end``
        // is NULL for indefinite contracts) — write empty string
        // rather than a stale previous value when the worksite has
        // no end date. This mirrors the indefinite-contract
        // semantics from §3.1.
        setDatepickerValue(endInput, formatDateForFlowbite(endIso));
    }
    // MONTHLY: intentionally no-op. Monthly jobs use ``monthly_day``
    // (1-28) + ``monthly_time`` only; there's no start/end date on
    // the Job to sync.
}

// ========================================================
// PR-4c-2 (contract refactor §6.4) — Facturation card swap
// ========================================================
//
// The « Facturation de la mission » card branches on the parent
// contract's billing_mode (OPEN editor vs FIXED_PERIODIC banner).
// First paint is server-rendered ; subsequent worksite changes
// trigger an HTMX swap of ``#job-billing-card-content`` via
// ``office:job_billing_card_partial`` (or its edit-mode variant).
//
// After every swap the per-radio panel toggle has to be re-bound
// on the freshly-injected DOM ; the listener uses event-delegation
// on the container so a single subscription survives swaps (the
// container itself is preserved by HTMX, only its children are
// replaced).
//
// Why event-delegation over per-radio listeners : the swapped
// HTML carries brand-new <input> nodes — any listener attached
// directly to the previous radios dies with them. A delegated
// listener on the outer #job-billing-card-content container
// (which is NOT replaced on innerHTML swaps — only its children
// are) catches the change events from every generation of swapped
// radios for free. Per-element flag on the container itself (rule 4
// of .cursor/rules/hx-boost-init.mdc) — a module-level bindOnce
// would short-circuit on the 2nd boost-nav visit because the flag
// survives the swap but the container node does not.

function _applyEmissionDayHintVisibility(container, grouping) {
    if (!container) return;
    // Per-radio help — show only the selected grouping's explanation.
    container.querySelectorAll('[data-grouping-hint]').forEach((hint) => {
        hint.hidden = hint.dataset.groupingHint !== grouping;
    });
    // Draft/emit detail — monthly grouping only.
    container.querySelectorAll('[data-job-emission-day-hint]').forEach((hint) => {
        hint.hidden = grouping !== 'monthly_grouped';
    });
}

// ── FIXED_PERIODIC forfait (JOB_BILLING_REFACTOR PR-D, 2026-05) ──────
// The periodic forfait moved from the WorkSite onto the Mission. The
// panel reuses the existing « Total TTC » machinery (data-total-ttc /
// data-total-source + data-vat-mirror), and adds a 12-checkbox months
// grid (VARIABLE periodicity) + an annualised estimation footer. The
// Total TTC + annualised are recomputed on every price keystroke.

const _FORFAIT_PERIODICITY_FACTOR = { monthly: 12, quarterly: 4, annual: 1 };
const _FORFAIT_MONTH_FALLBACK = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function _forfaitReadMonths(hidden) {
    if (!hidden || !hidden.value) return [];
    return hidden.value
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isInteger(n) && n >= 1 && n <= 12);
}

function _forfaitWriteMonths(hidden, months) {
    if (!hidden) return;
    const cleaned = Array.from(new Set(months))
        .filter((n) => Number.isInteger(n) && n >= 1 && n <= 12)
        .sort((a, b) => a - b);
    hidden.value = cleaned.join(',');
}

function _initForfaitMonthsGrid(container) {
    const grid = container.querySelector('[data-billing-variable-months-grid]');
    const hidden = container.querySelector('[data-fixed-months-input]');
    if (!grid || !hidden || grid._forfaitGridBound) return;
    grid._forfaitGridBound = true;

    const selected = new Set(_forfaitReadMonths(hidden));
    // Month labels are localised server-side (``{% month_abbrevs %}`` →
    // ``data-month-labels``) so the picker matches the UI language. Fall
    // back to English only if the attr is missing/malformed — never
    // hardcode user-visible month names in JS (i18n rule 5).
    const labelsAttr = (grid.dataset.monthLabels || '').split(',').map((s) => s.trim());
    const monthLabels = labelsAttr.length === 12 ? labelsAttr : _FORFAIT_MONTH_FALLBACK;
    grid.innerHTML = monthLabels.map((label, idx) => {
        const m = idx + 1;
        const checked = selected.has(m) ? 'checked' : '';
        return `
        <label class="inline-flex items-center gap-1 cursor-pointer rounded border border-gray-200 dark:border-gray-700 px-2 py-1">
            <input type="checkbox" class="form-checkbox" value="${m}" data-month-checkbox ${checked} autocomplete="off">
            <span class="text-xs text-gray-700 dark:text-gray-200">${label}</span>
        </label>`;
    }).join('');

    grid.addEventListener('change', (e) => {
        const target = e.target;
        if (!target || typeof target.matches !== 'function') return;
        if (!target.matches('[data-month-checkbox]')) return;
        const months = Array.from(
            grid.querySelectorAll('[data-month-checkbox]:checked'),
        ).map((el) => parseInt(el.value, 10));
        _forfaitWriteMonths(hidden, months);
        _recomputeForfaitAnnualised(container);
    });
}

function _applyForfaitMonthsVisibility(container) {
    const wrapper = container.querySelector('[data-billing-variable-months-wrapper]');
    const sel = container.querySelector('#id_fixed_periodicity');
    if (!wrapper || !sel) return;
    wrapper.hidden = sel.value !== 'variable';
}

function _forfaitFactor(container) {
    const sel = container.querySelector('#id_fixed_periodicity');
    const periodicity = sel ? sel.value : '';
    if (periodicity === 'variable') {
        const hidden = container.querySelector('[data-fixed-months-input]');
        const months = hidden && hidden.value
            ? hidden.value.split(',').filter((s) => s.trim() !== '')
            : [];
        return months.length || 1;
    }
    return _FORFAIT_PERIODICITY_FACTOR[periodicity] || 1;
}

function _forfaitFormatEuros(n) {
    // « 1 234,56 € » — French/Belgian convention (thin-space thousands
    // separator + comma decimal), matching the rest of the office UI.
    const rounded = Math.round(n * 100) / 100;
    const [intPart, decPart = '00'] = rounded.toFixed(2).split('.');
    const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
    return `${withSep},${decPart}\u00a0€`;
}

function _recomputeForfaitAnnualised(container) {
    const panel = container.querySelector('[data-forfait-price-panel]');
    if (!panel) return;
    const priceInput = panel.querySelector('[data-forfait-price]');
    const raw = priceInput ? String(priceInput.value).replace(',', '.') : '';
    const price = parseFloat(raw);
    const safePrice = Number.isFinite(price) ? price : 0;
    const factor = _forfaitFactor(container);

    const annualisedEl = container.querySelector('[data-forfait-annualised-total]');
    if (annualisedEl) annualisedEl.textContent = _forfaitFormatEuros(safePrice * factor);

    // Surface the multiplication (« 12 × 200,00 € ») so the office
    // understands the annual number. Append to the static base label
    // rather than replacing it (preserve the i18n string).
    const labelEl = container.querySelector('[data-forfait-annualised-label]');
    if (labelEl) {
        if (labelEl.dataset.baseLabel === undefined) {
            labelEl.dataset.baseLabel = labelEl.textContent.trim();
        }
        const base = labelEl.dataset.baseLabel;
        labelEl.textContent = (factor > 1 && safePrice > 0)
            ? `${base} (${factor} × ${_forfaitFormatEuros(safePrice)})`
            : base;
    }
}

function _initForfaitEditors(container) {
    if (!container) return;
    _initForfaitMonthsGrid(container);
    _applyForfaitMonthsVisibility(container);
    _recomputeForfaitAnnualised(container);
}

function _applyJobPricingPanelVisibility(container, value) {
    if (!container) return;
    container.querySelectorAll('[data-pricing-panel]').forEach((panel) => {
        const matches = panel.dataset.pricingPanel === value;
        panel.classList.toggle('hidden', !matches);
    });
    // Invoice grouping is an OPEN-mode concern only — the periodic
    // forfait bills once per closed period via its own cron, so the
    // grouping radios are hidden when « Fixed recurring package » is
    // the selected pricing model.
    container.querySelectorAll('[data-grouping-section]').forEach((sec) => {
        sec.hidden = value === 'fixed_periodic';
    });
    container.querySelectorAll('[data-job-pricing-radio]').forEach((label) => {
        const matches = label.dataset.jobPricingRadio === value;
        // The selected card gets the primary border + tinted bg ;
        // the others fall back to the neutral hover state. Kept
        // in sync with the server-side template's initial render
        // so an HTMX swap + a JS toggle produce identical DOM.
        label.classList.toggle('border-primary-500', matches);
        label.classList.toggle('bg-primary-50', matches);
        label.classList.toggle('dark:bg-primary-900/20', matches);
        label.classList.toggle('border-gray-200', !matches);
        label.classList.toggle('dark:border-gray-700', !matches);
        label.classList.toggle('hover:border-gray-300', !matches);
        label.classList.toggle('dark:hover:border-gray-600', !matches);
        label.classList.toggle('hover:bg-gray-50', !matches);
        label.classList.toggle('dark:hover:bg-gray-700/30', !matches);
    });
}

function _syncVatMirrorsAcrossPanels(container, sourceSelect) {
    const val = sourceSelect.value;
    container.querySelectorAll('select[data-vat-mirror]').forEach((sel) => {
        if (sel === sourceSelect) return;
        if (sel.value !== val) sel.value = val;
    });
    _recomputeTotalsTtcForAllPanels(container);
}

// Money parse/format/sanitize are imported from utils/forms/money.js
// (shared with credit-notes, invoice-detail and the global
// data-money-input sanitizer in app.js). The submitted value is
// converted back to a dot at submit time
// (``DecimalTextInput.value_from_datadict``) since USE_L10N=False
// makes ``DecimalField`` expect a dot.

function _recomputeTotalsTtcForAllPanels(container) {
    container.querySelectorAll('[data-total-ttc]').forEach((totalEl) => {
        const panel = totalEl.closest('[data-pricing-panel]');
        if (!panel) return;
        const sourceName = totalEl.dataset.totalSource;
        if (!sourceName) return;
        const priceInput = panel.querySelector(`input[name="${sourceName}"]`);
        const vatSelect = panel.querySelector('select[data-vat-mirror]');
        if (!priceInput || !vatSelect) {
            totalEl.value = '';
            return;
        }
        const price = _parseMoney(priceInput.value);
        const vatRate = parseFloat(vatSelect.value);
        if (Number.isNaN(price) || Number.isNaN(vatRate)) {
            totalEl.value = '';
            return;
        }
        const total = price * (1 + vatRate / 100);
        totalEl.value = _formatMoney(total);
    });
}

// Reverse direction of _recomputeTotalsTtcForAllPanels: the operator
// typed the VAT-inclusive total, so back-calculate the excl-VAT price
// and write it into the REAL (submitted) price field. Setting
// ``priceInput.value`` programmatically does not fire an ``input``
// event, so this never loops back into the HT → TTC recompute.
function _recomputeHtFromTtc(totalEl) {
    const panel = totalEl.closest('[data-pricing-panel]');
    if (!panel) return;
    const sourceName = totalEl.dataset.totalSource;
    if (!sourceName) return;
    const priceInput = panel.querySelector(`input[name="${sourceName}"]`);
    const vatSelect = panel.querySelector('select[data-vat-mirror]');
    if (!priceInput || !vatSelect) return;
    const raw = totalEl.value.trim();
    if (raw === '') {
        priceInput.value = '';
        return;
    }
    const total = _parseMoney(raw);
    const vatRate = parseFloat(vatSelect.value);
    if (Number.isNaN(total) || Number.isNaN(vatRate)) return;
    const ht = total / (1 + vatRate / 100);
    priceInput.value = _formatMoney(ht);
}

function bindJobBillingCardOnce() {
    const container = document.getElementById('job-billing-card-content');
    if (!container || container._jobBillingCardBound) return;
    container._jobBillingCardBound = true;

    container.addEventListener('change', (e) => {
        const target = e.target;
        if (!target || typeof target.matches !== 'function') return;
        if (target.matches('input[type="radio"][name="pricing_model"]')) {
            if (!target.checked) return;
            _applyJobPricingPanelVisibility(container, target.value);
            return;
        }
        if (target.matches('input[type="radio"][name="invoice_grouping"]')) {
            if (!target.checked) return;
            _applyEmissionDayHintVisibility(container, target.value);
            return;
        }
        // JOB_BILLING_REFACTOR PR-D — the forfait periodicity drives
        // the variable-months grid visibility + the annualised factor.
        if (target.matches('#id_fixed_periodicity')) {
            _applyForfaitMonthsVisibility(container);
            _recomputeForfaitAnnualised(container);
            return;
        }
        if (target.matches('select[data-vat-mirror]')) {
            _syncVatMirrorsAcrossPanels(container, target);
            return;
        }
    });

    container.addEventListener('input', (e) => {
        const target = e.target;
        if (!target || typeof target.matches !== 'function') return;

        const isTtc = target.matches('[data-total-ttc]');
        // ``forfait_price_excl_vat`` joins ``per_visit_price`` /
        // ``hourly_rate`` for the forward HT → Total TTC recompute.
        const isHt = target.matches('input[name="per_visit_price"], input[name="hourly_rate"], input[name="forfait_price_excl_vat"]');
        if (!isTtc && !isHt) return;

        // Normalize the decimal separator first so every downstream parse
        // and the eventual form submit see dot-decimal text.
        _sanitizeMoneyValue(target);

        // Reverse direction — the operator typed the VAT-inclusive total;
        // back-calculate the excl-VAT price into the real field. Handled
        // before the HT branch so the two never fight over the same edit.
        if (isTtc) {
            _recomputeHtFromTtc(target);
            if (target.dataset.totalSource === 'forfait_price_excl_vat') {
                _recomputeForfaitAnnualised(container);
            }
            return;
        }
        _recomputeTotalsTtcForAllPanels(container);
        if (target.matches('input[name="forfait_price_excl_vat"]')) {
            _recomputeForfaitAnnualised(container);
        }
    });

    // After every HTMX swap of the billing card (worksite change),
    // recompute Total TTC and re-sync radio card highlights on the
    // freshly-rendered panels. ``htmx:afterSwap`` fires on the swap
    // target itself — bind on the stable outer card AND the container
    // so both innerHTML swaps and full subtree replacements are
    // covered.
    const card = container.closest('#job-billing-card');
    const syncAfterBillingSwap = () => {
        const fresh = document.getElementById('job-billing-card-content');
        if (!fresh) return;
        const checked = fresh.querySelector('input[type="radio"][name="pricing_model"]:checked');
        if (checked) {
            _applyJobPricingPanelVisibility(fresh, checked.value);
        }
        const groupingChecked = fresh.querySelector('input[type="radio"][name="invoice_grouping"]:checked');
        if (groupingChecked) {
            _applyEmissionDayHintVisibility(fresh, groupingChecked.value);
        }
        _recomputeTotalsTtcForAllPanels(fresh);
        _initForfaitEditors(fresh);
    };
    if (card && !card._jobBillingCardSwapBound) {
        card._jobBillingCardSwapBound = true;
        card.addEventListener('htmx:afterSwap', syncAfterBillingSwap);
    }
    if (!container._jobBillingCardSwapBound) {
        container._jobBillingCardSwapBound = true;
        container.addEventListener('htmx:afterSwap', syncAfterBillingSwap);
    }

    const checked = container.querySelector('input[type="radio"][name="pricing_model"]:checked');
    if (checked) {
        _applyJobPricingPanelVisibility(container, checked.value);
    }
    const groupingChecked = container.querySelector('input[type="radio"][name="invoice_grouping"]:checked');
    if (groupingChecked) {
        _applyEmissionDayHintVisibility(container, groupingChecked.value);
    }
    _recomputeTotalsTtcForAllPanels(container);
    _initForfaitEditors(container);
}

// ========================================================
// RECURRENCE SECTIONS
// ========================================================

/**
 * PR-B step B.5 (2026-05) — drastically shrunk. The legacy
 * ``oneOffSection`` / ``monthlySection`` / ``weeklySection`` toggle
 * dance is GONE (the new Planning card handles its own sub-section
 * visibility via ``initPlanningCard``), and ``enforceBiweeklySingleSlot``
 * is GONE alongside JobSlot. What remains :
 *
 *   * The « Default Assignment » card is always visible.
 *   * Pricing fields re-paint when the billing mode changes.
 *
 * Kept as a separate function so callers (``_runAutoInit``, billing
 * mode change handlers, etc.) don't need to inline these three
 * idempotent DOM tweaks.
 */
function updateRecurrenceSections(_elements) {
    const defaultAssignmentSection = document.getElementById('default-assignment-section');
    if (defaultAssignmentSection) defaultAssignmentSection.classList.remove('hidden');

    if (currentBillingMode) updatePricingForBillingMode(currentBillingMode);
}

// ========================================================
// PRICING FIELDS
// ========================================================

function updatePricingFields(elements) {
    // PR-6c-5 (2026-05) — only 2 pricing modes left:
    // ``flat_per_visit`` and ``hourly``. The legacy ``fixed`` mode
    // (and its companion ``fixed_price`` input) was dropped along
    // with ``Job.PricingModel.FIXED``. The element key was kept as
    // ``fixedPriceField`` for now to avoid touching every caller —
    // it actually points at the per-visit-price input wrapper.
    const { pricingModelInput, fixedPriceField, hourlyRateField } = elements;
    const model = pricingModelInput ? pricingModelInput.value : 'flat_per_visit';
    if (fixedPriceField && hourlyRateField) {
        if (model === 'flat_per_visit') {
            fixedPriceField.style.display = '';
            hourlyRateField.style.display = 'none';
        } else if (model === 'hourly') {
            hourlyRateField.style.display = '';
            fixedPriceField.style.display = 'none';
        } else {
            fixedPriceField.style.display = '';
            hourlyRateField.style.display = '';
        }
    }
}

// ========================================================
// BILLING MODE - PRICING VISIBILITY
// ========================================================

let currentBillingMode = null;
let _translations = {};

export function updatePricingForBillingMode(billingMode) {
    currentBillingMode = billingMode;
    const pricingWrapper = document.getElementById('pricing-fields-wrapper');
    const sectionEl = document.getElementById('pricing-duration-section');
    const titleEl = document.getElementById('pricing-duration-title');

    if (pricingWrapper) pricingWrapper.style.display = '';
    const durationField = document.getElementById('default-duration-field');
    if (durationField) durationField.style.display = '';

    // JOB_BILLING_REFACTOR PR-3b (2026-05) — the legacy
    // ``billing_mode_override`` column is gone (replaced by the
    // ``invoice_grouping`` radio, rendered UNCONDITIONALLY on the
    // form). No more visibility toggle based on customer cascade
    // or recurrence — every Job picks its grouping explicitly.

    if (sectionEl) sectionEl.style.display = '';

    // PR-4c-2 (contract refactor §6.4) — the legacy
    // « Pricing & Duration » title was conditionally rewritten
    // between « Pricing » and « Pricing & Duration » depending
    // on the recurrence. The card now holds only the estimated
    // duration (pricing has moved to the new full-width
    // « Facturation de la mission » card below), and the title
    // is static « Estimated duration » in every state — the
    // recurrence-driven rewrite would now stomp on a label that
    // no longer reflects the card's contents. The
    // ``titleEl`` lookup is kept for back-compat with the
    // ``_translations`` payload (consumers of this module still
    // ship the strings), but we no longer mutate it.
}

export function getBillingMode() {
    return currentBillingMode;
}

// ========================================================
// CUSTOMER SEARCH PICKER
// ========================================================

export function initCustomerSearch(config, onCustomerSelected) {
    // Server-side customer search. The full roster is NOT shipped to
    // the page (it didn't scale: a large tenant bloated every job-form
    // load and the alphabetical list was truncated behind a "+N more"
    // footer). The dropdown queries ``api_customers_search`` (ACTIVE +
    // ON_HOLD) as the office types, so results render without a cap
    // regardless of roster size — the DB does the matching. A monotonic
    // ``searchSeq`` drops out-of-order responses so a slow earlier
    // request can't overwrite a newer one.
    const SEARCH_LIMIT = 20;
    const { translations = {}, initialCustomer = null } = config;
    const customerSearchInput = document.getElementById('customer-search-input');
    const customerHiddenInput = document.getElementById('id_customer');
    const customerResultsContainer = document.getElementById('customer-search-results');
    const customerClearBtn = document.getElementById('customer-clear-btn');
    let searchSeq = 0;
    let searchDebounce = null;

    if (!customerSearchInput || !customerHiddenInput || !customerResultsContainer) return;

    function toggleClearBtn() {
        if (!customerClearBtn) return;
        customerClearBtn.classList.toggle('hidden', !customerSearchInput.value);
    }

    // Pre-fill the input from the already-picked customer. Only ever set
    // on a POST re-render after a validation error (the backend injects
    // ``{id, name}`` so we don't need the roster to resolve the name);
    // on a fresh GET both ``initialCustomer`` and the hidden value are
    // empty. Selecting a customer carries no billing signal — the
    // pricing card is driven by the picked WorkSite (cf. getBillingMode).
    if (customerHiddenInput.value && initialCustomer
        && String(initialCustomer.id) === String(customerHiddenInput.value)) {
        customerSearchInput.value = initialCustomer.name;
    }
    toggleClearBtn();

    // Status helpers — kept inline (rather than importing
    // ``frontend/src/utils/ui/customer-picker.js``) to avoid threading the
    // module dependency into every consumer of ``initCustomerSearch``.
    // The shared i18n bundle (``window.CUSTOMER_PICKER_I18N``, populated
    // by ``templates/office/base_office.html``) keeps the badge labels
    // and tooltip text translated through fr/nl/en.
    function isCustomerOnHold(customer) {
        return customer && customer.status && customer.status !== 'active';
    }
    function statusBadgeHtml(customer) {
        const i18n = (typeof window !== 'undefined' && window.CUSTOMER_PICKER_I18N) || {};
        if (customer.status === 'on_hold') {
            return ' <span class="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium '
                + 'rounded bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">'
                + (i18n.on_hold || 'On hold') + '</span>';
        }
        if (customer.status === 'inactive') {
            // Defensive branch — the job form context only seeds ACTIVE
            // and ON_HOLD customers (see apps/office/views_create.py),
            // so this badge does not currently render here. Kept aligned
            // with the shared customer-picker helper and the customers
            // list (``badge-red``) for the day INACTIVE is surfaced.
            return ' <span class="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium '
                + 'rounded bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">'
                + (i18n.inactive || 'Inactive') + '</span>';
        }
        return '';
    }
    function renderResults(results, query) {
        if (!results.length) {
            const label = translations.noCustomers || 'No customers matching';
            const suffix = query ? ` "${query}"` : '';
            customerResultsContainer.innerHTML = `<div class="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">${label}${suffix}</div>`;
        } else {
            customerResultsContainer.innerHTML = results.map(customer => {
                const disabled = isCustomerOnHold(customer);
                // Disabled rows: no hover, lower opacity and
                // ``data-disabled="true"`` so the click and keyboard
                // handlers below skip them. Server-side ``JobForm.__init__``
                // is the authoritative gate (queryset = ACTIVE only); this
                // is a UX shortcut to avoid a round-trip with a confusing
                // inline error.
                const rowClasses = disabled
                    ? 'px-3 py-2 flex items-center cursor-default opacity-60'
                    : 'px-3 py-2 flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600';
                const disabledAttr = disabled ? ' data-disabled="true"' : '';
                return `
                <div class="${rowClasses}"
                     data-customer-id="${customer.id}" data-customer-name="${customer.name}"${disabledAttr}>
                    ${customerAvatarHtml(customer.name, customer.customer_type)}
                    <div class="flex-1 min-w-0 flex items-center">
                        <div class="text-sm font-medium text-gray-900 dark:text-white truncate">${customer.name}</div>${statusBadgeHtml(customer)}
                    </div>
                </div>`;
            }).join('');
        }
        customerResultsContainer.classList.remove('hidden');
    }

    // Debounced server-side search. Empty query (dropdown opened / input
    // cleared) fetches the top results immediately like the visit
    // drawer; keystrokes debounce so the office can type without firing
    // a request per character.
    function filterAndDisplay(query) {
        const q = (query || '').trim();
        clearTimeout(searchDebounce);
        const seq = ++searchSeq;
        // Anti-blink: only paint the spinner on the very first open (when
        // no result row is rendered yet). While typing or re-opening we
        // keep the previous results visible until the new ones arrive, so
        // the dropdown swaps content in place instead of flashing
        // spinner → results on every keystroke / fast response.
        if (!customerResultsContainer.querySelector('[data-customer-id]')) {
            customerResultsContainer.innerHTML = `<div class="px-3 py-3 text-center"><svg class="w-5 h-5 mx-auto animate-spin text-gray-300 dark:text-gray-500" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg></div>`;
        }
        customerResultsContainer.classList.remove('hidden');
        const delay = q.length === 0 ? 0 : 250;
        searchDebounce = setTimeout(async () => {
            const params = new URLSearchParams({
                q: q,
                limit: String(SEARCH_LIMIT),
                // ON_HOLD customers are surfaced (greyed out, non-selectable)
                // rather than silently dropped — same disabled-row UX the
                // roster build used.
                include: 'active,on_hold',
            });
            try {
                const resp = await fetch(`/office/api/customers/search/?${params.toString()}`);
                if (!resp.ok) throw new Error('customer search failed');
                const data = await resp.json();
                if (seq !== searchSeq) return;  // a newer search superseded this one
                renderResults(data.results || [], q);
            } catch (error) {
                if (seq !== searchSeq) return;
                console.error('Customer search error:', error);
                customerResultsContainer.innerHTML = `<div class="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">${translations.noCustomers || 'No customers matching'}</div>`;
            }
        }, delay);
    }

    function selectCustomer(customerId, customerName) {
        customerHiddenInput.value = customerId;
        customerSearchInput.value = customerName;
        customerResultsContainer.classList.add('hidden');
        if (onCustomerSelected) onCustomerSelected();
        // PR-6c (2026-05) — ``Customer.billing_mode`` is gone; the
        // pricing/override visibility is driven by the picked
        // WorkSite (cf. ``getBillingMode()``). Reset to no-grouping
        // so a stale override from a previous customer doesn't leak.
        updatePricingForBillingMode(null);
        // Clear the full validation state, not just the red border:
        // the inline cleanup that lived here used to leave behind the
        // ``<p class="field-error">`` message (e.g. "Ce champ est
        // obligatoire.") so the user saw a Customer field with a value
        // AND a "required" error underneath. ``clearFieldError`` removes
        // ``has-error``, the label colour AND the error <p> in one go.
        clearFieldError(customerSearchInput);
        toggleClearBtn();
    }

    // The focus listener below (re-)opens the dropdown whenever the
    // input gains focus while empty. After the user clicks the (×) clear
    // button we still want to focus the input for keyboard accessibility,
    // but NOT immediately re-pop the full customer list — that would
    // undo the action the user just took. Suppress for one tick.
    let suppressFocusOpen = false;

    function clearCustomerSelection() {
        customerSearchInput.value = '';
        customerHiddenInput.value = '';
        customerResultsContainer.classList.add('hidden');
        if (onCustomerSelected) onCustomerSelected();
        updatePricingForBillingMode(null, null);
        toggleClearBtn();
        suppressFocusOpen = true;
        customerSearchInput.focus();
        suppressFocusOpen = false;
    }

    if (customerClearBtn) {
        customerClearBtn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            clearCustomerSelection();
        });
    }

    let highlightedIndex = -1;

    function getVisibleOptions() {
        // Excludes disabled rows so ArrowUp/ArrowDown skip past them and
        // Enter never resolves to a non-active customer. Mirrors the
        // ``isItemDisabled`` plumbing in the shared ``SearchPicker``
        // component for consistency.
        return customerResultsContainer.querySelectorAll('[data-customer-id]:not([data-disabled="true"])');
    }

    function updateHighlight(options, newIndex) {
        options.forEach(opt => opt.classList.remove('bg-gray-100', 'dark:bg-gray-600'));
        highlightedIndex = newIndex;
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
            options[highlightedIndex].classList.add('bg-gray-100', 'dark:bg-gray-600');
            options[highlightedIndex].scrollIntoView({ block: 'nearest' });
        }
    }

    customerSearchInput.addEventListener('input', function (e) {
        highlightedIndex = -1;
        customerHiddenInput.value = '';
        if (onCustomerSelected) onCustomerSelected();
        updatePricingForBillingMode(null, null);
        toggleClearBtn();
        filterAndDisplay(e.target.value);
    });

    customerSearchInput.addEventListener('keydown', function (e) {
        const options = getVisibleOptions();
        if (!options.length || customerResultsContainer.classList.contains('hidden')) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            updateHighlight(options, highlightedIndex < options.length - 1 ? highlightedIndex + 1 : 0);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            updateHighlight(options, highlightedIndex > 0 ? highlightedIndex - 1 : options.length - 1);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedIndex >= 0 && highlightedIndex < options.length) {
                const opt = options[highlightedIndex];
                selectCustomer(opt.dataset.customerId, opt.dataset.customerName);
                highlightedIndex = -1;
            }
        } else if (e.key === 'Escape') {
            customerResultsContainer.classList.add('hidden');
            highlightedIndex = -1;
        }
    });

    function reopenDropdownForCurrentValue() {
        filterAndDisplay(customerSearchInput.value);
    }

    customerSearchInput.addEventListener('focus', function () {
        if (suppressFocusOpen) return;
        reopenDropdownForCurrentValue();
    });

    // Clicking back into an already-focused input (select customer →
    // click ×, which re-focuses the input → click back in) must re-open
    // the dropdown: the ``focus`` event only fires on the focus
    // *transition*, not on subsequent clicks, so without this the office
    // would have to click outside then back in to see the list.
    // Only act when the dropdown is currently CLOSED — on a first click
    // into the input the ``focus`` handler already opened it, and
    // re-firing here would launch a second fetch (the visible blink).
    customerSearchInput.addEventListener('click', function () {
        if (customerResultsContainer.classList.contains('hidden')) {
            reopenDropdownForCurrentValue();
        }
    });

    customerResultsContainer.addEventListener('click', function (e) {
        const option = e.target.closest('[data-customer-id]');
        // Disabled rows still have ``data-customer-id`` so the row can be
        // hovered for the tooltip, but clicking them must be a no-op.
        if (option && option.dataset.disabled !== 'true') {
            selectCustomer(option.dataset.customerId, option.dataset.customerName);
        }
    });

    document.addEventListener('click', function (e) {
        if (!customerSearchInput.contains(e.target) && !customerResultsContainer.contains(e.target)) {
            customerResultsContainer.classList.add('hidden');
            highlightedIndex = -1;
        }
    });
}

// ========================================================
// JOB-LEVEL CLEANER PICKER (unified for modal + full page)
// ========================================================
//
// Config-driven differences:
//   - availability.enabled: fetch and show Available/Busy badges
//   - availability.pollInterval: auto-refresh interval (ms)

export function initJobCleanerPicker(config) {
    const {
        translations = {},
        availability = {},
        initialSelectedCleaners = [],
        worksites = [],
    } = config;

    const pickerEl = document.getElementById('job-cleaner-picker');
    const addBtn = document.getElementById('job-add-cleaner-btn');
    const clearBtn = document.getElementById('job-clear-all-btn');
    const dropdown = document.getElementById('job-cleaner-dropdown');
    const searchInput = document.getElementById('job-cleaner-search');
    const listEl = document.getElementById('job-cleaner-list');
    const chipsContainer = document.getElementById('job-selected-chips');
    const hiddenSelect = document.getElementById('job-assigned-employees-select');

    if (!pickerEl || !addBtn || !dropdown || !listEl || !chipsContainer || !hiddenSelect) return;

    // Server-side search cap (mirrors the drawer cleaner picker). Beyond
    // this the office types to refine — there is NO truncation footer,
    // the list simply scrolls. The DB does the filtering, so the picker
    // scales regardless of roster size (no full roster in the dropdown).
    const SEARCH_LIMIT = 20;
    const selectedCleaners = new Map();
    let lastResults = [];
    let searchSeq = 0;
    let searchDebounce = null;

    // FAQ Q41 — current worksite drives subcontractor-cleaner
    // eligibility. Re-read on every render so the picker stays
    // in sync if the office switches worksites mid-flow.
    function getCurrentWorksiteId() {
        return resolveCurrentWorksiteId();
    }

    // Pre-select existing assigned employees (edit mode). PR-C — these
    // arrive as full objects (``initialSelectedCleaners``), not ids
    // resolved against a roster. We stash ``subcontractorCompanyId`` on
    // every map entry so the worksite-change eligibility re-sweep
    // (FAQ Q41) works without the roster.
    initialSelectedCleaners.forEach(cleaner => {
        const name = getCleanerName(cleaner);
        const initials = cleaner.initials || name.substring(0, 2).toUpperCase();
        selectedCleaners.set(String(cleaner.id), {
            name,
            photoUrl: cleaner.photo_url || '',
            initials,
            subcontractorCompanyId: cleaner.subcontractor_company_id || null,
        });
    });

    // Hidden <select> for form submission. A ``<select multiple>`` only
    // submits the OPTIONS it actually contains, so — with no roster — we
    // rebuild it from the live selection on every change instead of
    // pre-rendering every cleaner.
    function populateHiddenSelect() {
        hiddenSelect.innerHTML = '';
        selectedCleaners.forEach((data, id) => {
            const opt = document.createElement('option');
            opt.value = id;
            opt.textContent = (data && data.name) || '';
            opt.selected = true;
            hiddenSelect.appendChild(opt);
        });
    }

    // Resolve the current schedule (date / time / duration) from the form
    // so the server search can flag Available / Busy / Absent per result —
    // the same inputs the old availability poller read. Returns empty
    // strings when the schedule is incomplete (the search then omits the
    // availability legs and just matches on name).
    function getScheduleParams() {
        let dateStr = '';
        let timeStr = '';
        let duration = '';

        const recurrenceEl = document.getElementById('id_recurrence');
        const recurrence = recurrenceEl ? recurrenceEl.value : '';
        const durationInput = document.getElementById('id_default_duration_minutes');
        if (durationInput && durationInput.value) duration = durationInput.value;

        if (recurrence === 'one_off') {
            const dateInput = document.getElementById('id_one_off_date');
            const timeInput = document.getElementById('id_one_off_time');
            if (dateInput && dateInput.value) dateStr = dateInput.value;
            if (timeInput && timeInput.value) timeStr = timeInput.value;
        } else if (recurrence === 'recurring' || recurrence === 'monthly') {
            // The Planning card writes its anchor time into the hidden
            // ``start_time`` input for every recurring frequency.
            const timeInput = document.querySelector('input[name="start_time"]');
            if (timeInput && timeInput.value) timeStr = timeInput.value;
        }
        return { date: dateStr, time: timeStr, duration };
    }

    function showEmptyState() {
        listEl.innerHTML = `<li class="px-3 py-3 text-center"><svg class="w-5 h-5 mx-auto text-gray-300 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg></li>`;
    }

    const TOOLTIP_PREFIX = 'job-cleaner';

    // Render server search results (no client-side slice, no truncation
    // footer — the list scrolls). Called by ``filterAndDisplay`` after a
    // fetch, and directly (no refetch) after a selection toggle or a
    // worksite change so checkmarks / eligibility repaint instantly.
    function renderResults(results) {
        listEl.innerHTML = '';
        // Drop the previous render's tooltip divs to keep the body-level
        // container small and prevent stale targets after worksite or
        // availability changes (Flowbite keeps Tooltip instances in a
        // global registry — the next reinit call re-binds fresh ones).
        clearCleanerPickerTooltips(TOOLTIP_PREFIX);

        if (!results || results.length === 0) {
            listEl.innerHTML = `<li class="px-3 py-3 text-center text-sm text-gray-500 dark:text-gray-400">${translations.noCleaners || 'No cleaners found'}</li>`;
            return;
        }

        const wsId = getCurrentWorksiteId();
        const declaredCompanyIds = getDeclaredCompanyIdsForWorksite(worksites, wsId);

        results.forEach(cleaner => {
            const name = cleaner.name || getCleanerName(cleaner);
            const initials = cleaner.initials || name.substring(0, 2).toUpperCase();
            const isSelected = selectedCleaners.has(String(cleaner.id));
            const isEligible = !wsId || isCleanerEligibleForWorksite(cleaner, declaredCompanyIds, worksites, wsId);

            let avatarHtml;
            if (cleaner.photo_url) {
                avatarHtml = `<img src="${cleaner.photo_url}" alt="${name}" class="w-7 h-7 rounded-full object-cover mr-2.5 flex-shrink-0">`;
            } else {
                avatarHtml = `<div class="w-7 h-7 rounded-full flex items-center justify-center mr-2.5 flex-shrink-0 bg-primary-600"><span class="text-xs font-medium text-white">${initials}</span></div>`;
            }

            // FAQ Q41 — orange ineligibility badge mirrors the visual
            // pattern of the existing Available/Busy/Absent badges so
            // the office reads "this row is locked, here's why" at a
            // glance. The grouped hint below the picker carries the
            // one-click "Declare X" CTA so users still have a path
            // forward without leaving the form. ``whitespace-nowrap +
            // shrink-0`` keeps the badge on a single line and
            // prevents it from being squeezed when the row is narrow.
            // Tooltip = Flowbite (per the project rule
            // ``flowbite-tooltips.mdc``); the body-level helper above
            // appends the matching ``<div role="tooltip">`` outside
            // the dropdown so it isn't clipped by the scroll
            // container.
            let eligibilityBadge = '';
            if (!isEligible) {
                const tooltipText = (translations.subcontractorNotDeclaredTooltip
                    || 'Company not declared on the selected worksite')
                    .replace('{company}', cleaner.subcontractor_company_name || '');
                const tipId = addCleanerPickerTooltip(TOOLTIP_PREFIX, `inel-${cleaner.id}`, tooltipText);
                eligibilityBadge = `<span data-tooltip-target="${tipId}" data-tooltip-placement="top" class="ml-2 shrink-0 whitespace-nowrap px-1.5 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 cursor-help">${translations.subcontractorNotDeclared || 'Not declared'}</span>`;
            }

            // Availability badge (only if data has been fetched).
            // Skipped when the row is already greyed out for
            // ineligibility — stacking two warning badges would
            // muddle the message. Same Flowbite-tooltip path as the
            // ineligibility badge above, only attached when there's
            // ``busy_info`` to show.
            let availBadge = '';
            if (isEligible && typeof cleaner.available !== 'undefined') {
                const avail = {
                    available: cleaner.available !== false,
                    is_absent: !!cleaner.is_absent,
                    busy_info: cleaner.busy_info || null,
                };
                {
                    let availClasses;
                    let availLabel;
                    if (avail.available) {
                        availClasses = 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
                        availLabel = translations.available || 'Available';
                    } else if (avail.is_absent) {
                        availClasses = 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';
                        availLabel = translations.absent || 'Absent';
                    } else {
                        availClasses = 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
                        availLabel = translations.busy || 'Busy';
                    }
                    let tooltipAttrs = '';
                    let cursorClass = '';
                    if (!avail.available && avail.busy_info) {
                        const tipId = addCleanerPickerTooltip(TOOLTIP_PREFIX, `avail-${cleaner.id}`, avail.busy_info);
                        tooltipAttrs = ` data-tooltip-target="${tipId}" data-tooltip-placement="top"`;
                        cursorClass = ' cursor-help';
                    }
                    availBadge = `<span${tooltipAttrs} class="ml-2 shrink-0 whitespace-nowrap px-1.5 py-0.5 text-xs rounded-full ${availClasses}${cursorClass}">${availLabel}</span>`;
                }
            }

            const li = document.createElement('li');
            const ineligibleCls = isEligible
                ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600'
                : 'opacity-60 cursor-default';
            li.className = `cleaner-option px-2 py-1.5 flex items-center justify-between gap-2 ${ineligibleCls} ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`;
            li.dataset.cleanerId = String(cleaner.id);
            li.dataset.cleanerName = name;
            li.dataset.cleanerPhoto = cleaner.photo_url || '';
            li.dataset.cleanerInitials = initials;
            // Carried onto the selection map entry so the worksite-change
            // eligibility re-sweep (FAQ Q41) has the subcontractor id
            // without re-querying a roster we no longer ship.
            li.dataset.cleanerSubco = cleaner.subcontractor_company_id
                ? String(cleaner.subcontractor_company_id)
                : '';
            if (!isEligible) li.dataset.ineligible = 'true';
            // ``min-w-0 flex-1`` on the inner flex row + ``truncate``
            // on the name span = name elides with "…" when long
            // enough to collide with the badge, instead of wrapping
            // to a second line and pushing the row to double height.
            // Same pattern as the chips container in ``updateUI``.
            li.innerHTML = `
                <div class="flex items-center min-w-0 flex-1">${avatarHtml}<span class="text-sm font-medium text-gray-900 dark:text-white truncate">${name}</span>${eligibilityBadge}${availBadge}</div>
                <svg class="w-4 h-4 text-blue-500 check-icon shrink-0 ${isSelected ? '' : 'hidden'}" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>`;
            listEl.appendChild(li);
        });

        // Drop any stale grouped hint from a prior page generation.
        // Proactive « declare subcontractor » CTAs are not rendered on
        // the job form — declare subs from the worksite detail page.
        const staleHint = document.getElementById('job-cleaner-ineligible-hint');
        if (staleHint) staleHint.remove();

        // Wire the freshly-rendered ``data-tooltip-target`` triggers
        // to their body-level tooltip divs. ``reinitFlowbiteWidgets``
        // is idempotent (Flowbite's registry uses ``override: true``)
        // so re-renders don't pile up stale Tooltip instances.
        if (window.reinitFlowbiteWidgets) {
            window.reinitFlowbiteWidgets(listEl);
        }
    }

    // Debounced server-side search. Replaces the old client-side filter
    // over the injected roster: the DB does the matching (scales to any
    // roster size) and results render without a truncation footer. A
    // monotonic ``searchSeq`` drops out-of-order responses so a slow
    // earlier request can't overwrite a newer one.
    function filterAndDisplay(query) {
        const q = query || '';
        clearTimeout(searchDebounce);
        const seq = ++searchSeq;
        listEl.innerHTML = `<li class="px-3 py-3 text-center"><svg class="w-5 h-5 mx-auto animate-spin text-gray-300 dark:text-gray-500" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg></li>`;
        // Empty query (dropdown open / cleared input) fetches immediately
        // like the drawer cleaner picker; keystrokes debounce. This is the
        // difference that made the open feel laggy vs the visit drawer.
        const delay = q.length === 0 ? 0 : 250;
        searchDebounce = setTimeout(async () => {
            const params = new URLSearchParams({ q: q, limit: String(SEARCH_LIMIT) });
            if (availability.enabled) {
                const sched = getScheduleParams();
                if (sched.date && sched.time) {
                    params.set('date', sched.date);
                    params.set('time', sched.time);
                    if (sched.duration) params.set('duration', sched.duration);
                }
            }
            try {
                const resp = await fetch(`/office/api/cleaners/search/?${params.toString()}`);
                if (!resp.ok) throw new Error('cleaner search failed');
                const data = await resp.json();
                if (seq !== searchSeq) return;  // a newer search superseded this one
                lastResults = data.results || [];
                renderResults(lastResults);
            } catch (error) {
                if (seq !== searchSeq) return;
                console.error('Cleaner search error:', error);
                listEl.innerHTML = `<li class="px-3 py-3 text-center text-sm text-gray-500 dark:text-gray-400">${translations.noCleaners || 'No cleaners found'}</li>`;
            }
        }, delay);
    }

    function updateUI() {
        chipsContainer.innerHTML = '';
        selectedCleaners.forEach((data, id) => {
            const name = typeof data === 'object' ? data.name : data;
            const photoUrl = typeof data === 'object' ? data.photoUrl : '';
            const initials = (typeof data === 'object' && data.initials)
                ? data.initials
                : (name || '').substring(0, 2).toUpperCase();

            let avatarHtml;
            if (photoUrl) {
                avatarHtml = `<img src="${photoUrl}" alt="" class="w-5 h-5 rounded-full object-cover mr-1.5 flex-shrink-0">`;
            } else {
                avatarHtml = `<div class="w-5 h-5 rounded-full flex items-center justify-center mr-1.5 flex-shrink-0 bg-primary-600"><span class="text-[10px] font-medium text-white">${initials}</span></div>`;
            }

            const chip = document.createElement('span');
            chip.className = 'inline-flex items-center pl-1 pr-2 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
            chip.dataset.cleanerId = id;
            chip.innerHTML = `${avatarHtml}<span class="cleaner-name">${name}</span><button type="button" class="remove-cleaner ml-1 inline-flex items-center justify-center w-3.5 h-3.5 text-blue-400 hover:text-blue-600" data-remove-id="${id}"><svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg></button>`;
            chipsContainer.appendChild(chip);
        });

        // Rebuild the hidden <select> from the live selection — options
        // for cleaners picked from the server search don't exist until we
        // add them, and a <select multiple> only submits options it holds.
        populateHiddenSelect();

        if (clearBtn) clearBtn.classList.toggle('hidden', selectedCleaners.size === 0);
    }

    // Init. The dropdown is closed at load, so we do NOT hit the search
    // API on every page render — the fetch fires when the office opens
    // the dropdown (addBtn handler) or types.
    populateHiddenSelect();
    updateUI();

    // Refresh availability badges if the schedule changes WHILE the
    // dropdown is open (otherwise the next open re-fetches anyway, so
    // there's nothing to refresh).
    ['id_one_off_date', 'id_one_off_time'].forEach(function(id) {
        var el = document.getElementById(id);
        if (el) el.addEventListener('change', function () {
            if (!dropdown.classList.contains('hidden')) {
                filterAndDisplay(searchInput ? searchInput.value : '');
            }
        });
    });

    // Event listeners
    addBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        dropdown.classList.toggle('hidden');
        if (!dropdown.classList.contains('hidden') && searchInput) {
            searchInput.value = '';
            filterAndDisplay('');
            searchInput.focus();
        }
    });

    document.addEventListener('click', function (e) {
        if (!pickerEl.contains(e.target)) dropdown.classList.add('hidden');
    });

    if (searchInput) {
        searchInput.addEventListener('input', function () {
            filterAndDisplay(this.value.toLowerCase());
        });
    }

    listEl.addEventListener('click', function (e) {
        const option = e.target.closest('.cleaner-option');
        if (!option) return;
        // FAQ Q41 — silently ignore clicks on greyed-out rows
        // (server-side validator stays the gate, but we don't even
        // let the office try to select them in the normal flow).
        if (option.dataset.ineligible === 'true') return;
        const id = option.dataset.cleanerId;
        const name = option.dataset.cleanerName;
        const photoUrl = option.dataset.cleanerPhoto || '';
        const initials = option.dataset.cleanerInitials || name.substring(0, 2).toUpperCase();
        const subcontractorCompanyId = option.dataset.cleanerSubco || null;

        // Multi-select: a job can have several default cleaners. Toggle the
        // clicked cleaner and keep the dropdown open so the office can add or
        // remove several in a row.
        if (selectedCleaners.has(id)) {
            selectedCleaners.delete(id);
        } else {
            selectedCleaners.set(id, { name, photoUrl, initials, subcontractorCompanyId });
            clearFieldError(hiddenSelect);
            clearPickerError(pickerEl);
        }
        updateUI();
        // Repaint the checkmark from the cached results (no refetch); the
        // dropdown stays open for the next toggle.
        renderResults(lastResults);
    });

    chipsContainer.addEventListener('click', function (e) {
        const removeBtn = e.target.closest('[data-remove-id]');
        if (!removeBtn) return;
        selectedCleaners.delete(removeBtn.dataset.removeId);
        updateUI();
    });

    if (clearBtn) {
        clearBtn.addEventListener('click', function () {
            selectedCleaners.clear();
            updateUI();
        });
    }

    // FAQ Q41 — when the office switches worksite, recompute
    // eligibility: the dropdown re-renders with the new
    // greyed-out rows + grouped hint, and any chips that turned
    // ineligible are dropped (silently — the office sees them
    // disappear from the chips row, the next save would have
    // bounced anyway).
    const wsSelect = document.getElementById('id_work_site');
    if (wsSelect) {
        wsSelect.addEventListener('change', function () {
            const wsId = wsSelect.value;
            const declared = getDeclaredCompanyIdsForWorksite(worksites, wsId);
            // Drop now-ineligible selected chips. The subcontractor id
            // was stashed on the map entry at selection / pre-fill time
            // (no roster to look it up in anymore).
            const toRemove = [];
            selectedCleaners.forEach((data, id) => {
                const pseudo = {
                    id,
                    subcontractor_company_id:
                        (data && typeof data === 'object') ? data.subcontractorCompanyId : null,
                };
                if (wsId && !isCleanerEligibleForWorksite(pseudo, declared, worksites, wsId)) {
                    toRemove.push(id);
                }
            });
            toRemove.forEach(id => selectedCleaners.delete(id));
            updateUI();
            renderResults(lastResults);  // re-evaluate eligibility on cached results
        });
    }
}

// ========================================================
// CHECKLIST EDITOR
// ========================================================

// The Mission owns its checklist (the contract-level default was
// dropped). The editor is the shared textarea + live preview + import
// dropdown (``checklist-editor.js``), identical to the former worksite
// form. The textarea (``#id_checklist_items``) is the source of truth ;
// ``JobForm.save()`` bridges it back to the ``checklist_template``
// JSONField server-side, so no client-side serialization is needed.
export function initChecklistEditor() {
    const form = document.getElementById('job-form');
    initSharedChecklistEditor(form || document);
}

// ========================================================
// MAIN INIT
// ========================================================

/**
 * @param {Object} config
 * @param {Object|null} [config.initialCustomer] - {id, name} of the
 *   already-picked customer on a POST re-render; the search picker
 *   queries ``api_customers_search`` server-side for everything else.
 * @param {Array}    config.locations
 * @param {Array}    config.worksites
 * @param {Array}    [config.initialSelectedCleaners]
 * @param {Array}    config.serviceTypesJson
 * @param {Object}   config.translations
 * @param {Function} [config.onServiceTypeApplied]
 * @param {string}   [config.formId]
 * @param {Object}   [config.availability] - { enabled, pollInterval }
 */
export function initJobForm(config) {

    const {
        locations,
        worksites,
        initialSelectedCleaners = [],
        translations = {},
        onServiceTypeApplied,
        formId = 'job-form',
        availability = {},
        isEdit = false,
    } = config;

    currentBillingMode = null;
    _translations = translations;

    updatePricingForBillingMode(null);

    window.clearFieldError = clearFieldError;
    window.clearPickerError = clearPickerError;

    initFormValidation(formId);

    const recurrenceSelect = document.getElementById('id_recurrence');
    const pricingModelInput = document.getElementById('id_pricing_model');
    // PR-6c-5 (2026-05) — the legacy ``fixed_price`` field/wrapper is
    // gone. ``perVisitPriceField`` / ``perVisitPriceInput`` are kept
    // under the legacy key names (``fixedPriceField`` / ``fixedPriceInput``)
    // so the closures in ``createServiceTypeHandler`` and
    // ``updatePricingFields`` keep working without per-call rewiring.
    const fixedPriceField = document.getElementById('per-visit-price-field')
        || document.getElementById('fixed-price-field');
    const hourlyRateField = document.getElementById('hourly-rate-field');
    const defaultDurationField = document.getElementById('default-duration-field');
    const durationInput = document.querySelector('[name="default_duration_minutes"]');
    const fixedPriceInput = document.querySelector('[name="per_visit_price"]');
    const hourlyRateInput = document.querySelector('[name="hourly_rate"]');
    const customerHiddenInput = document.getElementById('id_customer');

    const sectionElements = {
        recurrenceSelect, pricingModelInput,
        fixedPriceField, hourlyRateField, defaultDurationField,
        durationInput, fixedPriceInput, hourlyRateInput,
    };

    const tracker = createEditTracker();
    if (durationInput) durationInput.addEventListener('input', () => tracker.mark('duration'));
    if (fixedPriceInput) fixedPriceInput.addEventListener('input', () => tracker.mark('per_visit_price'));
    if (hourlyRateInput) hourlyRateInput.addEventListener('input', () => tracker.mark('hourly_rate'));

    const applyDefaults = createServiceTypeHandler(tracker, sectionElements, onServiceTypeApplied);

    // Slices A § 6.4 + F § 7.4 / § 7.5 — visits-without-mobile-app
    // CIaO verrou. Re-evaluate the mobile-tracking lock AND the
    // cleaner-picker lock whenever the office picks a different
    // service_type — a movable-property switch can take the Job out
    // of CIaO scope (and vice-versa), which gates BOTH the
    // ``mobile_tracking_disabled`` checkbox (Slice A) AND the
    // cleaner picker (Slice F, externally-handled branch).
    // Slice F § 7.5 fix (May 2026 follow-up) — read ``serviceTypesJson``,
    // NOT ``config.data.serviceTypes``. The two coexist in the codebase:
    //
    // * ``initJobFormPage(config)`` (the page entry-point) receives
    //   ``config.data.serviceTypes`` straight from the server-built
    //   ``_build_form_page_config`` payload.
    // * ``initJobForm(innerConfig)`` (this function) is called by
    //   ``initJobFormPage`` with a flattened object that aliases the
    //   list as ``serviceTypesJson`` (see line 3158:
    //   ``serviceTypesJson: config.data.serviceTypes || []``).
    //
    // Reading ``config.data.serviceTypes`` here therefore always
    // resolved to ``undefined`` → ``[]``, which made the
    // ``_resolveTrackingMode`` `find` return ``undefined`` for every
    // service-type id → ``inCiaoScope`` was forced false → mode
    // collapsed to ``'free'`` → the verrou's ``else`` branch ran and
    // **silently re-enabled** the server-side ``disabled`` checkbox
    // (and its ``cleaner-picker`` sibling). Same mistake in both
    // helpers below; the fix is to read ``serviceTypesJson`` from the
    // de-structured ``config`` (already destructured at line 1470,
    // also surfaced earlier in this scope as ``serviceTypesJson``
    // via the ``createServiceTypeHandler`` closure).
    const _serviceTypesForVerrou = config.serviceTypesJson || [];
    const doUpdateMobileTracking = () => updateMobileTrackingState(
        worksites,
        _serviceTypesForVerrou,
        translations,
    );
    const doUpdateCleanerPickerLock = () => updateCleanerPickerLockState(
        worksites,
        _serviceTypesForVerrou,
        translations,
    );
    // Slice F.20 — service-type picker greys out movable options
    // when the selected worksite is in CIaO scope. Wired to the
    // worksite ``change`` event below + the initial render at the
    // bottom of this function.
    const doUpdateServiceTypePickerLock = () => updateServiceTypePickerLockState(
        worksites,
        _serviceTypesForVerrou,
        translations,
    );

    // Slice F.23 — keep the snapshot hidden inputs in lockstep with
    // the current selection so a parallel-tab edit between GET-render
    // and POST is detected server-side. See the helper docstring for
    // the full rationale.
    const doSyncCiaoSnapshotFields = () => _syncCiaoSnapshotFields(
        _serviceTypesForVerrou,
        worksites,
    );

    document.addEventListener('change', function (e) {
        if (e.target && e.target.name === 'service_type') {
            applyDefaults(e.target.value);
            doUpdateMobileTracking();
            doUpdateCleanerPickerLock();
            doSyncCiaoSnapshotFields();

            // Worksite Lifecycle (May 2026, PO Marie Laurent UX
            // follow-up — second-round fix) — async pass: BULK
            // re-fetch ``is_movable_property`` for every service
            // type so the picker verrou re-evaluates ALL options
            // against fresh data, not only the selected row. The
            // single-id refresh used in the first round only
            // covered the selected row, leaving greyed-out options
            // permanently frozen on stale cached data (the office
            // can't pick a greyed-out row to trigger its own
            // refresh — chicken-and-egg). Silent on network
            // error — POST-time snapshot check stays authoritative.
            refreshAllServiceTypesMovableFlags(_serviceTypesForVerrou, () => {
                doUpdateMobileTracking();
                doUpdateCleanerPickerLock();
                doUpdateServiceTypePickerLock();
                doSyncCiaoSnapshotFields();
            });
        }
    });

    // Slice F.22 follow-up — free-mode hand-toggle of the Office-only
    // checkbox must hide / show the Checklist card live, even though
    // the verrou is NOT triggered (no worksite / movable service →
    // ``updateMobileTrackingState`` returns mode='free' without
    // touching the checkbox). The doUpdateMobileTracking() call below
    // re-evaluates the help text + card visibility in lockstep so the
    // help paragraph and the checklist card never drift out of sync.
    const mtdCheckboxForCardSync = document.getElementById('id_mobile_tracking_disabled');
    if (mtdCheckboxForCardSync && !mtdCheckboxForCardSync._mtdCardSyncBound) {
        mtdCheckboxForCardSync._mtdCardSyncBound = true;
        mtdCheckboxForCardSync.addEventListener('change', () => {
            doUpdateMobileTracking();
        });
    }

    const doUpdateLocations = () => updateLocations(customerHiddenInput, locations);
    const doUpdateWorkSites = () => updateWorkSites(customerHiddenInput, worksites);
    const doApplyWorkSiteLocationLock = () => applyWorkSiteLocationLock(worksites);
    const doUpdateCustomerScopedFields = () => {
        doUpdateLocations();
        doUpdateWorkSites();
        // Customer change wipes the worksite selection (different
        // tenant, different worksites). Re-evaluating the lock
        // immediately re-enables Location so the office can pick
        // freely until they bind a worksite of the new customer.
        doApplyWorkSiteLocationLock();

        // Worksite Lifecycle (May 2026, third-round) — bulk-refresh
        // the worksite cache so the new customer's picker shows
        // fresh availability (any worksite that tab 2 archived /
        // attached a live job to since GET-render is hidden;
        // anything tab 2 unarchived / cancelled re-appears).
        // Skipped in edit mode (the picker is ``disabled`` there).
        if (!isEdit) {
            refreshAllWorksitesAvailability(worksites, doUpdateWorkSites);
        }
    };
    if (!isEdit) {
        initCustomerSearch(config, doUpdateCustomerScopedFields);
    }

    // Live-apply the Location lock when the office picks / clears
    // a worksite from the dropdown. The initial-render call is
    // right after this block.
    //
    // PR3 §5.9 — auto-fill schedule dates from the worksite's
    // contract dates on the SAME ``change`` event. Date override
    // is intentionally NOT called on initial render: an existing
    // Job's dates (edit form, or saved-then-redisplayed create
    // form on validation error) must not be silently rewritten.
    const workSiteSelect = document.getElementById('id_work_site');
    if (workSiteSelect) {
        // Worksite Lifecycle (May 2026, third-round) — pre-refresh
        // the picker on focus so a parallel-tab archive / job-attach
        // is reflected the moment the office opens the dropdown,
        // not only after they've already picked something. The
        // fetch is async so the dropdown opens with the cache as
        // it currently is; the re-render happens ~one round-trip
        // later (which is invisible to the office because they're
        // still scanning the list at that point).
        //
        // ``isEdit`` mode keeps the picker ``disabled`` (PR3 §5.7)
        // so re-rendering it would be moot — skip the round-trip.
        // The bulk fetch is also redundant in edit mode (the
        // bound worksite must stay in the picker even with a live
        // job under edit; the bulk endpoint would mark it
        // ``has_live_job=true`` and the picker filter would hide
        // it without the pinning logic the JSON payload carries).
        //
        // Per-element JS-property flag (rule 3 of hx-boost-init):
        // module-level state survives boost but the SELECT does
        // not, so we'd silently skip wiring on the second visit.
        if (!isEdit && !workSiteSelect._wsBulkFocusBound) {
            workSiteSelect._wsBulkFocusBound = true;
            workSiteSelect.addEventListener('focus', () => {
                refreshAllWorksitesAvailability(worksites, () => {
                    doUpdateWorkSites();
                    // Re-evaluate the verrou + snapshot in case the
                    // refresh changed in_ciao_scope on the currently
                    // selected worksite (rare via focus alone, but
                    // free with the same callback).
                    doUpdateMobileTracking();
                    doUpdateCleanerPickerLock();
                    doUpdateServiceTypePickerLock();
                    doSyncCiaoSnapshotFields();
                });
            });
        }

        workSiteSelect.addEventListener('change', () => {
            doApplyWorkSiteLocationLock();
            applyWorkSiteDateOverride(worksites);
            // JOB_BILLING_REFACTOR PR-C (2026-05) — the Mission billing
            // card no longer depends on the worksite, so picking a
            // worksite does NOT swap (or reset) the pricing inputs.
            // Checklist is owned by the Mission now — picking a
            // worksite no longer touches the checklist editor.
            // Slices A + F — picking a new worksite can flip the
            // Job between the three modes (free / mobile-required /
            // externally-handled); re-evaluate both the checkbox
            // verrou and the cleaner-picker block.
            doUpdateMobileTracking();
            doUpdateCleanerPickerLock();
            // Slice F.20 — also re-evaluate which service-type
            // options are compatible with the new worksite (CIaO-
            // scoped worksites grey out movable-property services).
            // Order matters: this runs LAST so that if the lock
            // greys out the currently-selected service type and
            // clears it, the resulting synthetic 'change' event
            // re-runs the service_type handler above (which itself
            // re-runs ``doUpdateMobileTracking`` /
            // ``doUpdateCleanerPickerLock``) — converging on a
            // coherent state without further wiring.
            doUpdateServiceTypePickerLock();
            // Slice F.23 — refresh the snapshot hidden inputs so
            // the server can detect a parallel-tab edit at POST
            // time. Also runs on the synthetic 'change' that the
            // service_type handler dispatches above (via the
            // document-level listener).
            doSyncCiaoSnapshotFields();

            // Worksite Lifecycle (May 2026, PO Marie Laurent UX
            // follow-up) — async pass: re-fetch BOTH the worksite's
            // CIaO scope AND the bulk service-type movable-property
            // flags. We fire both in parallel because the picker
            // verrou cross-references them: the greyed-out state of
            // a service type depends on (a) the selected worksite's
            // ``in_ciao_scope`` AND (b) the service type's
            // ``is_movable_property``. A cross-tab edit to either
            // input must re-run the verrou. Without the bulk
            // service-type fetch, unticking ``is_movable_property``
            // in another tab would leave the previously-greyed
            // option PERMANENTLY locked because the office can't
            // re-select it (chicken-and-egg).
            //
            // The synchronous block above keeps instant UI feedback;
            // these two fetches refine the verrou ~one round-trip
            // later. Silent on network error — Slice F.23 POST-time
            // snapshot check stays authoritative.
            const _rerunVerrouAfterAsync = () => {
                doUpdateMobileTracking();
                doUpdateCleanerPickerLock();
                doUpdateServiceTypePickerLock();
                doSyncCiaoSnapshotFields();
            };
            refreshWorksiteCiaoScope(
                worksites, workSiteSelect.value, _rerunVerrouAfterAsync,
            );
            refreshAllServiceTypesMovableFlags(
                _serviceTypesForVerrou, _rerunVerrouAfterAsync,
            );
            // Worksite Lifecycle (May 2026, third-round) — also bulk
            // refresh worksite availability so a parallel-tab archive
            // / job-attach that turned the just-picked worksite
            // unavailable is caught immediately. ``updateWorkSites``
            // re-renders the picker minus any unavailable rows AND
            // clears the selection if the office's just-picked
            // worksite is now gone (with a synthetic 'change' that
            // re-runs every downstream handler).
            if (!isEdit) {
                refreshAllWorksitesAvailability(worksites, () => {
                    doUpdateWorkSites();
                    _rerunVerrouAfterAsync();
                });
            }
        });
    }

    // PR3 §5.7 — a ``disabled`` <select> is omitted from FormData,
    // so the Location lock would cause the form to fail with
    // "this field is required". Re-enable it just before submit
    // so the value POSTs as expected. ``capture: true`` runs this
    // before any validating submit handlers further down the tree.
    const formEl = document.getElementById(formId);
    if (formEl) {
        formEl.addEventListener('submit', () => {
            const ls = document.getElementById('id_location');
            if (ls && ls.dataset.lockedByWorksite === 'true') {
                ls.disabled = false;
            }
            // Slice F.23 — defensive re-sync at submit time. Covers
            // the corner case where the office picked a value via a
            // path that bypasses the per-field 'change' listener
            // (autofill, paste, scripted assignment) so the snapshot
            // never got refreshed since GET render. ``capture: true``
            // (set on the submit listener registration above) makes
            // sure this runs BEFORE any submit-time validators.
            _syncCiaoSnapshotFields(_serviceTypesForVerrou, worksites);
        }, { capture: true });
    }

    if (recurrenceSelect) {
        recurrenceSelect.addEventListener('change', () => {
            updateRecurrenceSections(sectionElements);
            // JOB_BILLING_REFACTOR PR-3a (2026-05) — the call is
            // kept (it still updates the contract-billing-card paint
            // for OPEN / FIXED_PERIODIC) but the legacy
            // ``billing_mode_override`` visibility toggle is gone
            // (PR-3b dropped the column entirely).
            updatePricingForBillingMode(getBillingMode());
            // PR3 §5.9 — re-apply the worksite date override against
            // the new recurrence's date inputs. If the office picks
            // a worksite then switches recurrence, the newly-revealed
            // section should still inherit the contract dates rather
            // than expose empty inputs.
            applyWorkSiteDateOverride(worksites);
        });
    }

    doUpdateLocations();
    doUpdateWorkSites();
    // Initial-render lock: on the edit form, the job may already
    // carry a worksite — sync Location and freeze it so the office
    // doesn't see a deceptively editable address dropdown.
    doApplyWorkSiteLocationLock();
    // PR-4c-2 (contract refactor §6.4) — bind the « Facturation de
    // la mission » card's per-radio panel toggle. Per-element flag
    // on ``#job-billing-card-content`` (rule 4 of hx-boost-init.mdc)
    // so boost-nav re-inits the listener on each fresh container.
    bindJobBillingCardOnce();

    // Slices A + F — initial-render verrou CIaO. On the edit form
    // the server-side ``JobEditForm.__init__`` already set
    // ``field.disabled`` and the matching ``initial`` value if the
    // bound Job was in CIaO scope (Layer 2 / 3); this call keeps
    // the JS state aligned (and writes the matching help text). On
    // the create form, this is the very first evaluation. Cleaner
    // picker block re-runs in lockstep.
    doUpdateMobileTracking();
    doUpdateCleanerPickerLock();
    // Slice F.20 — initial sweep of the service-type picker so a
    // page reload (or an edit-form render) shows the correct grey-
    // out state from the first paint, not after the first
    // interaction.
    doUpdateServiceTypePickerLock();
    // Slice F.23 — initial sync of the snapshot hidden inputs from
    // the currently-selected (bound or pre-filled) service_type /
    // work_site. The server-side ``__init__`` already populated the
    // initials from the bound instance on edit; this call adds the
    // create-form path where the dropdowns may be pre-selected via
    // URL query params (e.g. ``?work_site=…``) but no 'change' event
    // has fired yet. (The ``?from_quote_line=…`` autofill deep-link
    // was removed with the quote-module deletion on 2026-05-21.)
    doSyncCiaoSnapshotFields();
    updateRecurrenceSections(sectionElements);
    updatePricingFields(sectionElements);

    // Job-level cleaner picker. PR-C (2026-06) — no roster: the picker
    // searches server-side and pre-selects from ``initialSelectedCleaners``
    // (objects, edit mode only).
    initJobCleanerPicker({
        translations, availability, worksites,
        initialSelectedCleaners,
    });

    // Checklist editor
    initChecklistEditor();

    // Quick-add subcontractor modal (FAQ Q41 fix, May 2026).
    // The server-side validators
    // (``apps.compliance.services.find_subcontractor_assignment_gaps_for_cleaners``
    // → ``JobForm.clean()`` / ``validate_slot_subcontractor_assignments``)
    // render a per-row "Declare on this worksite" button when a
    // SUBCONTRACTOR-typed cleaner's company is missing from the
    // selected worksite's active assignments. Each button carries the
    // worksite + company ids; we open the canonical
    // ``office:worksite_subcontractor_assign`` modal pre-filled with
    // those ids and re-submit the parent Job form on success so the
    // office never has to re-enter anything they already typed.
    initQuickAddSubcontractorModal(formId);

    return {
        applyDefaults,
        tracker,
        updateLocations: doUpdateLocations,
        updateWorkSites: doUpdateWorkSites,
        applyWorkSiteLocationLock: doApplyWorkSiteLocationLock,
        updateRecurrenceSections: () => updateRecurrenceSections(sectionElements),
        updatePricingFields: () => updatePricingFields(sectionElements),
    };
}


let _quickAddSubcontractorModal = null;
let _quickAddBoundFormId = null;
let _pendingResubmitFormId = null;

function initQuickAddSubcontractorModal(formId) {
    const modalEl = document.getElementById('quick-add-subcontractor-modal');
    if (!modalEl) return;

    if (!_quickAddSubcontractorModal) {
        _quickAddSubcontractorModal = createDynamicModal({
            modalId: 'quick-add-subcontractor-modal',
            onContentInjected: function () {
                if (typeof window.initCustomSelects === 'function') {
                    window.initCustomSelects();
                }
                if (typeof window.reinitFlowbiteWidgets === 'function') {
                    window.reinitFlowbiteWidgets(modalEl);
                }
            },
            onSuccess: function (_data, helper) {
                helper.close();
                if (_pendingResubmitFormId) {
                    const form = document.getElementById(_pendingResubmitFormId);
                    _pendingResubmitFormId = null;
                    if (form) {
                        // ``requestSubmit`` triggers the same pipeline as a
                        // user click on the Save button (validation +
                        // ``data-ajax-form`` interception), so the AJAX
                        // posting path the office is already on is
                        // preserved and the new ``SubcontractorAssignment``
                        // gets exercised by the server-side gate
                        // immediately.
                        if (typeof form.requestSubmit === 'function') {
                            form.requestSubmit();
                        } else {
                            form.submit();
                        }
                    }
                }
            },
        });
    }

    if (_quickAddBoundFormId === formId) return;
    _quickAddBoundFormId = formId;

    document.addEventListener('click', function (evt) {
        const btn = evt.target && evt.target.closest && evt.target.closest('[data-quick-add-subcontractor]');
        if (!btn) return;
        evt.preventDefault();

        const form = document.getElementById(formId);
        const urlTemplate = form && form.dataset.quickAddSubcontractorUrlTemplate;
        if (!urlTemplate || !_quickAddSubcontractorModal) return;

        const worksiteId = btn.dataset.worksiteId;
        const companyId = btn.dataset.companyId;
        if (!worksiteId || !companyId) return;

        // The template was reversed with ``pk=0`` server-side
        // (``data-quick-add-subcontractor-url-template``), so swap the
        // ``/0/`` placeholder for the actual worksite id. Belt-and-
        // suspenders ``encodeURIComponent`` on the company id even
        // though it must be an integer, in case a future change ever
        // surfaces a slug here.
        const url = urlTemplate.replace('/0/', `/${encodeURIComponent(worksiteId)}/`)
            + `?subcontractor=${encodeURIComponent(companyId)}`;

        const companyName = btn.dataset.companyName || '';
        const worksiteLabel = btn.dataset.worksiteLabel || '';
        const title = companyName && worksiteLabel
            ? `${companyName} → ${worksiteLabel}`
            : 'Declare subcontractor on worksite';

        _pendingResubmitFormId = formId;
        _quickAddSubcontractorModal.open(url, title);
    });
}


// ============================================================================
// EDIT-JOB SUBMIT HANDLER (schedule-change preview + impact-confirmation
// modal). Uses the canonical ``window.showConfirmModal`` helper for the
// modal, with a per-element bind-once flag + bypass-flag pattern. The
// create flow has no equivalent handler — it submits natively.
//
// Flow on submit:
//   1. Capture initial schedule values + slot snapshot at module init time
//      (the moment the page loads, i.e. before the office touches anything).
//   2. On submit: if ``_bypassScheduleCheck`` set → native submit. Else
//      compare current schedule state to the snapshot — if unchanged, also
//      native submit (no preview round-trip needed).
//   3. If changed → POST the form data to ``office:job_preview_schedule``
//      (dry-run). Endpoint returns ``{cancelled, created, preserved,
//      out_of_window_overrides}`` summarising what would happen on save.
//   4. If both ``cancelled`` and ``created`` are zero → no impact, native
//      submit. Otherwise show a ``window.showConfirmModal`` with a composed
//      ``messageHtml`` describing the impact; "Confirm" sets bypass +
//      submits, "Cancel" leaves the form open.
//   5. On 400 with ``error`` body (form-level validation failure on the
//      preview side) → bypass + submit so the real save surfaces the
//      authoritative per-field errors. On other non-2xx or fetch reject →
//      "Could not preview" warning modal with a "Save anyway" affordance.
// ============================================================================

// Per-element bind flag (``form._editSubmitBound``) — see
// ``initJobEditSubmit`` below. Same rationale as createSubmitBound: a
// module-level flag breaks boost re-navigation; ``dataset.*`` would
// break Back/Forward via HTMX history-snapshot survival.

const _SCHEDULE_FIELD_IDS = [
    'id_one_off_date', 'id_one_off_time', 'id_start_date', 'id_end_date',
];
// Hidden RRULE-primitive fields kept in sync by the Planning card
// (``syncToHiddenFields``). They must ALL participate in the
// schedule-change diff: a frequency / weekday / positions change
// cancels + recreates future visits server-side, so the impact
// preview modal has to fire for them exactly like for a date change.
const _SCHEDULE_FIELD_NAMES = [
    'monthly_day', 'recurrence', 'frequency', 'interval',
    'byweekday', 'bymonth', 'start_time', 'monthly_weekday_positions',
];

function _captureSlotSnapshotForEdit() {
    const snapshot = [];
    document.querySelectorAll('#slots-tbody tr.slot-row').forEach(function (row) {
        const deleteCheck = row.querySelector('input[name$="-DELETE"]');
        if (deleteCheck && deleteCheck.checked) return;
        const wd = row.querySelector('select[name$="-weekday"]');
        const st = row.querySelector('input[name$="-start_time"]');
        const dm = row.querySelector('input[name$="-duration_minutes"]');
        snapshot.push({
            weekday: wd ? wd.value : '',
            start_time: st ? st.value : '',
            duration_minutes: dm ? dm.value : '',
        });
    });
    // Stringify here so equality comparison in ``hasScheduleChanged`` is a
    // single ``===`` instead of a deep-walk.
    return JSON.stringify(snapshot);
}

function _captureInitialScheduleValues() {
    const values = {};
    _SCHEDULE_FIELD_IDS.forEach(function (id) {
        const el = document.getElementById(id);
        if (el) values[id] = el.value;
    });
    _SCHEDULE_FIELD_NAMES.forEach(function (name) {
        const el = document.querySelector('[name="' + name + '"]');
        if (el) values[name] = el.value;
    });
    return values;
}

function _hasScheduleChanged(initialValues, initialSlotSnapshot) {
    for (const key in initialValues) {
        const el = document.getElementById(key) || document.querySelector('[name="' + key + '"]');
        if (el && el.value !== initialValues[key]) return true;
    }
    if (_captureSlotSnapshotForEdit() !== initialSlotSnapshot) return true;
    return false;
}

const _PRICE_FIELD_IDS = {
    per_visit_price: 'id_per_visit_price',
    hourly_rate: 'id_hourly_rate',
    // JOB_BILLING_REFACTOR PR-D (2026-05) — forfait price participates
    // in the price-change confirmation diff like the other pricing
    // columns (``effective_vat_rate`` is read by name below).
    forfait_price_excl_vat: 'id_forfait_price_excl_vat',
};

function _readPriceFieldValues(initialSnapshot) {
    const values = {};
    Object.keys(initialSnapshot || {}).forEach(function (key) {
        if (key === 'effective_vat_rate') {
            const el = document.querySelector('[name="effective_vat_rate"]');
            values[key] = el ? el.value : '';
            return;
        }
        if (key === 'pricing_model') {
            // Rendered as a radio group (billing card), not a select with
            // an ``id_pricing_model`` — read the checked radio, else the
            // snapshot value would diff against '' on every submit.
            const radio = document.querySelector('input[name="pricing_model"]:checked');
            values[key] = radio ? radio.value : '';
            return;
        }
        const id = _PRICE_FIELD_IDS[key];
        const el = id ? document.getElementById(id) : null;
        values[key] = el ? el.value : '';
    });
    return values;
}

// Compare snapshot vs DOM values tolerantly: the server snapshot is
// dot-decimal (``str(Decimal)``) while localized money inputs render
// comma-decimal (e.g. ``145,00``). Numeric-looking values are compared
// as numbers so an untouched form never false-positives the
// price-change modal; non-numeric values (``pricing_model``) fall back
// to plain string equality.
function _normalizePriceValue(v) {
    const s = String(v == null ? '' : v).trim().replace(',', '.');
    if (s === '') return '';
    const f = parseFloat(s);
    return Number.isNaN(f) ? s : String(f);
}

function _hasPriceChanged(initialSnapshot) {
    if (!initialSnapshot) return false;
    const current = _readPriceFieldValues(initialSnapshot);
    return Object.keys(initialSnapshot).some(function (key) {
        return _normalizePriceValue(current[key]) !== _normalizePriceValue(initialSnapshot[key]);
    });
}

function _needsPriceChangeConfirm(form, translations) {
    if (!translations.hasDoneOccurrences || !translations.initialPriceSnapshot) return false;
    const confirmEl = form.querySelector('#id_confirm_price_change');
    if (confirmEl && confirmEl.checked) return false;
    return _hasPriceChanged(translations.initialPriceSnapshot);
}

function _confirmPriceChange(form, translations, onConfirm) {
    if (typeof window.showConfirmModal !== 'function') {
        onConfirm();
        return;
    }
    window.showConfirmModal({
        title: translations.priceChangeTitle,
        message: translations.priceChangeMessage,
        confirmText: translations.priceChangeConfirm,
        cancelText: translations.priceChangeCancel,
        type: 'warning',
        onConfirm: function () {
            const confirmEl = form.querySelector('#id_confirm_price_change');
            if (confirmEl) confirmEl.checked = true;
            onConfirm();
        },
    });
}

function _buildScheduleImpactHtml(data, t) {
    let msg = '';
    if (data.cancelled > 0) {
        msg += '<strong>' + data.cancelled + '</strong> ' + escapeHtml(t.futureVisitsCancelled);
    }
    if (data.cancelled > 0 && data.created > 0) {
        msg += ' ' + escapeHtml(t.and) + ' ';
    }
    if (data.created > 0) {
        msg += '<strong>' + data.created + '</strong> ' + escapeHtml(t.newVisitsCreated);
    }
    msg += ' ' + escapeHtml(t.basedOnUpdatedSchedule);
    if (data.preserved > 0) {
        msg += '<br><span class="text-xs text-gray-500 dark:text-gray-400">'
            + data.preserved + ' ' + escapeHtml(t.overridesPreserved) + '</span>';
    }
    if (data.out_of_window_overrides > 0) {
        msg += '<br><span class="text-xs text-amber-700 dark:text-amber-400 font-medium">'
            + '⚠ ' + data.out_of_window_overrides + ' '
            + escapeHtml(t.outOfWindowOverrides) + '</span>';
    }
    return msg;
}

/**
 * Wire the edit-Job submit pipeline (snapshot diff → schedule-change
 * preview → confirm modal → real submit). Bind-once for the SPA lifetime.
 *
 * @param {object} config
 * @param {string} [config.formId='job-form']
 * @param {object} config.translations  See ``_build_edit_submit_config``
 *                                     in ``apps/office/views/jobs.py``.
 *                                     Must include ``previewUrl``.
 */
export function initJobEditSubmit(config) {
    const { formId = 'job-form', translations } = config || {};
    if (!translations || !translations.previewUrl) return;

    const form = document.getElementById(formId);
    if (!form) return;

    if (form._editSubmitBound) return;
    form._editSubmitBound = true;

    // Snapshot now — before the office touches anything. Anything that
    // diverges from this baseline at submit time triggers a preview call.
    const initialValues = _captureInitialScheduleValues();
    const initialSlotSnapshot = _captureSlotSnapshotForEdit();

    let bypassScheduleCheck = false;
    const proceed = function () {
        bypassScheduleCheck = true;
        form.submit();
    };

    form.addEventListener('submit', function (e) {
        if (bypassScheduleCheck) return;

        const scheduleChanged = _hasScheduleChanged(initialValues, initialSlotSnapshot);

        if (!scheduleChanged && _needsPriceChangeConfirm(form, translations)) {
            e.preventDefault();
            _confirmPriceChange(form, translations, proceed);
            return;
        }

        if (!scheduleChanged) return;
        e.preventDefault();

        const submitBtn = form.querySelector('button[type="submit"]');
        const origText = submitBtn ? submitBtn.textContent : '';
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = translations.checkingImpact;
        }
        const restore = function () {
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = origText;
            }
        };

        const formData = new FormData(form);
        const csrf = formData.get('csrfmiddlewaretoken');

        fetch(translations.previewUrl, {
            method: 'POST',
            headers: { 'X-CSRFToken': csrf },
            body: formData,
        })
            .then(function (r) {
                // Try to parse JSON for both 2xx and 4xx so we can distinguish
                // "form has field errors" (let server render them inline)
                // from "true network/server failure" (offer Save anyway).
                return r.json().then(
                    function (data) { return { ok: r.ok, status: r.status, data: data }; },
                    function () { return { ok: r.ok, status: r.status, data: null }; },
                );
            })
            .then(function (resp) {
                restore();

                // Form-level validation failure surfaced by the preview
                // endpoint — bypass and let the real submit render the
                // authoritative per-field errors.
                if (resp.status === 400 && resp.data && resp.data.error) {
                    proceed();
                    return;
                }

                // Any other non-2xx without a recognizable error body is a
                // real server failure. Fail-soft with a warning modal.
                if (!resp.ok || !resp.data) {
                    if (typeof window.showConfirmModal !== 'function') { proceed(); return; }
                    window.showConfirmModal({
                        title: translations.modalTitle,
                        message: translations.modalPreviewFailed,
                        confirmText: translations.modalSaveAnyway,
                        type: 'warning',
                        onConfirm: proceed,
                    });
                    return;
                }

                const data = resp.data;
                // No occurrences cancelled, none created → no user-visible
                // impact, just save without bothering them.
                if ((data.cancelled || 0) === 0 && (data.created || 0) === 0) {
                    proceed();
                    return;
                }

                if (typeof window.showConfirmModal !== 'function') { proceed(); return; }
                window.showConfirmModal({
                    title: translations.modalTitle,
                    messageHtml: _buildScheduleImpactHtml(data, translations),
                    confirmText: translations.modalConfirmChange,
                    type: 'info',
                    onConfirm: proceed,
                });
            })
            .catch(function () {
                restore();
                if (typeof window.showConfirmModal !== 'function') { proceed(); return; }
                window.showConfirmModal({
                    title: translations.modalTitle,
                    message: translations.modalPreviewFailed,
                    confirmText: translations.modalSaveAnyway,
                    type: 'warning',
                    onConfirm: proceed,
                });
            });
    });
}


// ============================================================================
// PAGE-LEVEL ENTRY POINT (Phase 3.3c). Single bootstrap consumed by the
// ``data-auto-init="jobFormPage"`` attribute on ``<form id="job-form">``.
//
// Reads two ``json_script`` blocks (all built server-side):
//   - ``job-form-page-config``    — required. data + translations + flags.
//   - ``job-form-edit-config``    — present on the edit variant only
//     (schedule-change preview pipeline). The create flow submits
//     natively, so it has no per-flow config block.
//
// The split is deliberate: ``job-form-page-config`` is the shared
// bootstrap (customers/locations/worksites/categories + the assigned
// cleaners), and the edit submit config is the thin per-flow concern
// (preview URL + impact-modal copy). Keeping them separate lets future
// variants (templated job, duplicate job, ...) reuse the page bootstrap
// without copy-pasting the edit submit dict.
// ============================================================================

// Per-element bind flag (``form._formPageBound``) — see
// ``initJobFormPage`` below. The previous module-level flag silently
// caused "no cleaners pre-selected on edit after boost-navigation"
// (the assigned cleaner chip rendered correctly on F5 / first-tab
// load, but disappeared when the office navigated to a second job
// edit page in the same session via boost — the ``initJobForm`` call
// was short-circuited and the chip never built). Stored as a JS
// property (not ``dataset.*``) for the Back/Forward correctness
// reason documented in ``.cursor/rules/hx-boost-init.mdc § 3``.

function _readJsonScript(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
        return JSON.parse(el.textContent);
    } catch {
        return null;
    }
}

/**
 * Boot the entire Job form page (shared module + per-flow submit
 * pipeline). Idempotent — safe to call from ``htmx:afterSwap`` etc.
 *
 * @param {object} config  See ``apps/office/views_create.py::
 *                         _build_form_page_config`` for the shape.
 * @returns {object|undefined}  The ``initJobForm`` API handle, or
 *                              ``undefined`` if the form root is
 *                              missing (e.g. called during teardown).
 */
// =============================================================================
// « Type de service » picker
// =============================================================================
//
// The « Type de service » lives solely on the Mission and is REQUIRED
// for every mission (server-side ``JobForm.clean()`` / ``JobEditForm
// .clean()`` enforce it). This just populates the ``ContractCategory``
// combobox from the catalogue, preserving any Django-prerendered value.
function initCategoryPicker(categories) {
    const select = document.getElementById('id_category');
    if (!select) return null;

    // Per-element guard (rule 3 of hx-boost-init) — the form node is
    // GC'd on every #main-content swap, so a re-init must re-populate
    // the SELECT.
    if (select._categoryPickerBound) return null;
    select._categoryPickerBound = true;

    // Populate the <select> from the catalogue. Preserve the
    // currently-selected value if Django pre-rendered it (edit form,
    // or POST-rendered create form on validation error).
    const currentValue = select.value || '';
    const placeholder = select.querySelector('option[value=""]');
    while (select.options.length > (placeholder ? 1 : 0)) {
        select.remove(placeholder ? 1 : 0);
    }
    (categories || []).forEach((cat) => {
        const opt = document.createElement('option');
        opt.value = String(cat.id);
        opt.textContent = cat.name;
        if (String(cat.id) === String(currentValue)) opt.selected = true;
        select.appendChild(opt);
    });

    return null;
}


export function initJobFormPage(config) {
    if (!config || !config.data) return;

    const formId = config.formId || 'job-form';
    const form = document.getElementById(formId);
    if (!form) return;

    if (form._formPageBound) return;
    form._formPageBound = true;

    const formApi = initJobForm({
        // PR (2026-06) — the customer roster is no longer shipped; the
        // search picker queries ``api_customers_search`` server-side.
        // Only the already-picked customer (POST re-render) comes down so
        // the input can pre-fill its name without a lookup.
        initialCustomer: config.data.initialCustomer || null,
        locations: config.data.locations || [],
        worksites: config.data.worksites || [],
        // PR-C (2026-06) — the full roster is no longer shipped; only the
        // already-assigned cleaners (edit mode) come down as objects. The
        // dropdown searches server-side via ``api_cleaners_search``.
        initialSelectedCleaners: config.data.initialSelectedCleaners || [],
        serviceTypesJson: config.data.serviceTypes || [],
        // « Type de service » catalogue surfaced by
        // ``_get_form_context_data`` → ``_build_form_page_config``.
        // Each entry is ``{id, name}``. Consumed by ``initCategoryPicker``
        // to populate the ``<select>`` options.
        categories: config.data.categories || [],
        isEdit: !!config.isEdit,
        orgCiaoEnabled: !!config.orgCiaoEnabled,
        recurrence: config.recurrence || '',
        translations: config.translations || {},
        formId: formId,
        availability: config.availability || { enabled: false },
        initialSelectedCleanerIds: config.initialSelectedCleanerIds || [],
        // The checklist is owned by the Mission and edited via the
        // textarea editor ; neither the ServiceType nor the WorkSite
        // change handler touches it.
        onServiceTypeApplied: function (_defaults) {},
    });

    // Populate the « Type de service » combobox from the catalogue.
    initCategoryPicker(config.data.categories || []);

    if (config.initialBillingMode) {
        updatePricingForBillingMode(config.initialBillingMode);
    }

    // Wire the edit-flow submit pipeline (schedule-change preview).
    // No-op if its ``json_script`` block isn't on the page. The create
    // flow submits natively — server-side validation is the single
    // source of truth, and the cleaner picker surfaces availability
    // inline at selection time (PR-D: the legacy submit-time
    // conflict-check modal was removed with the JobSlot model).
    if (config.isEdit) {
        const editTranslations = _readJsonScript('job-form-edit-config');
        if (editTranslations) {
            initJobEditSubmit({ formId: formId, translations: editTranslations });
        }
    }

    return formApi;
}

// Planning Refactor PR-B step B.3b (2026-05) ──────────────────────────
// New Planning card interactivity (mode toggle + freq tabs + weekday
// chips + sub-field → hidden-form-field sync).
//
// Companion to ``templates/office/jobs/_form_content.html`` (B.3a) :
// the template emits a single ``#planning-card`` element with data-*
// attributes carrying the initial RRULE-primitive state (read off the
// bound form), a visible UI (mode toggle / freq tabs / weekday chips /
// per-tab sub-fields), and a hidden block of Django form fields. This
// function (a) hydrates the visible UI from the data-* attributes on
// page load, (b) wires interactions so the hidden form fields stay
// in sync as the user mutates the UI, and (c) writes the RRULE-
// primitive values that the new generator path (``apps/jobs/services/
// generator.py::_generate_recurring``, PR-B step B.1) consumes at
// occurrence-creation time.
//
// HX-BOOST GUARD : per ``.cursor/rules/hx-boost-init.mdc`` rule 4,
// the bind flag lives on the DOM element (``card._planningCardBound``)
// — NOT on a module-level let — because the card is replaced on
// every ``#main-content`` swap and a module flag would short-circuit
// re-init on the new node.

const PLANNING_WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
const PLANNING_WEEKEND_CODES = ['SA', 'SU'];
const PLANNING_FREQUENCIES = ['daily', 'weekly', 'twice_monthly', 'monthly', 'yearly'];
const PLANNING_MODES = ['one_off', 'recurring'];

/**
 * Wire the Planning card UI to the hidden form fields.
 * Reads initial state from ``#planning-card`` data-* attributes,
 * hydrates the visible UI, then keeps the hidden RRULE-primitive
 * fields in sync as the user interacts.
 *
 * Idempotent — bails immediately if already bound on the current
 * DOM node (see hx-boost-init.mdc rule 4 for the per-element-flag
 * pattern used here).
 */
export function initPlanningCard() {
    const card = document.getElementById('planning-card');
    if (!card || card._planningCardBound) return;

    // Hidden form fields — Django ModelForm reads from these on POST.
    // PR-B step B.5 (2026-05) — dropped ``hMonthlyTime`` ; the legacy
    // ``monthly_time`` hidden input is no longer rendered (model
    // field is dropped by migration 0098). The visible « monthly
    // time » sub-field now writes to ``hStartTime`` directly.
    const hRecurrence = card.querySelector('[name="recurrence"]');
    const hFrequency = card.querySelector('[name="frequency"]');
    const hInterval = card.querySelector('[name="interval"]');
    const hByWeekday = card.querySelector('[name="byweekday"]');
    const hByMonth = card.querySelector('[name="bymonth"]');
    const hStartTime = card.querySelector('[name="start_time"]');
    const hMonthlyDay = card.querySelector('[name="monthly_day"]');
    const hMonthlyPositions = card.querySelector('[name="monthly_weekday_positions"]');
    if (!hRecurrence || !hFrequency) return; // form not on this page

    card._planningCardBound = true;
    const modeLocked = card.dataset.planningModeLocked === 'true';

    // Visible widgets
    const modeBtns = Array.from(card.querySelectorAll('[data-planning-mode]'));
    const modePanels = Array.from(card.querySelectorAll('.planning-mode-panel'));
    const freqBtns = Array.from(card.querySelectorAll('[data-planning-freq]'));
    const freqPanels = Array.from(card.querySelectorAll('.planning-freq-content'));
    const chips = Array.from(card.querySelectorAll('[data-weekday-chip]'));

    const subfields = {
        dailyInterval: card.querySelector('#planning-daily-interval'),
        dailyWeekend: card.querySelector('#planning-daily-include-weekend'),
        dailyTime: card.querySelector('#planning-daily-time'),
        weeklyInterval: card.querySelector('#planning-weekly-interval'),
        weeklyTime: card.querySelector('#planning-weekly-time'),
        twiceWeekday: card.querySelector('#planning-twice-weekday'),
        twicePositions: card.querySelector('#planning-twice-positions'),
        twiceTime: card.querySelector('#planning-twice-time'),
        monthlyInterval: card.querySelector('#planning-monthly-interval'),
        monthlyDay: card.querySelector('#planning-monthly-day'),
        monthlyTime: card.querySelector('#planning-monthly-time'),
        yearlyDay: card.querySelector('#planning-yearly-day'),
        yearlyMonth: card.querySelector('#planning-yearly-month'),
        yearlyTime: card.querySelector('#planning-yearly-time'),
    };

    // Per-chip state (instead of relying on DOM class as the source
    // of truth — class-based reads get racy when Tailwind utility
    // classes overlap with toggle-marker classes).
    const activeChips = new Set();

    // ─── helpers ─────────────────────────────────────────────────
    // ACTIVE_BTN_CLASSES (B.3 segmented-toggle paint classes) removed
    // in PR-B step B.4a — the mode toggle is now a native radiogroup
    // so the « selected » paint is handled by Tailwind ``has-[:checked]``
    // utilities directly on the wrapping label in the template.
    const ACTIVE_TAB_CLASSES = ['border-primary-500', 'text-primary-700'];
    const INACTIVE_TAB_CLASSES = ['border-transparent', 'text-gray-500'];
    const ACTIVE_CHIP_CLASSES = ['bg-primary-600', 'text-white', 'border-primary-600'];
    const INACTIVE_CHIP_CLASSES = ['bg-white', 'text-gray-700', 'border-gray-300'];

    function paintMode(mode) {
        // PR-B step B.4a — the mode toggle is now a pair of native
        // radio inputs (``[data-planning-mode]`` carried on the input
        // itself, not the wrapping label), so the « visible state »
        // is just ``input.checked``. Tailwind ``has-[:checked]:*``
        // classes on the wrapper label paint the « selected » border
        // and background, so we don't have to toggle classes by hand.
        modeBtns.forEach(btn => {
            const isOn = btn.dataset.planningMode === mode;
            btn.checked = isOn;
        });
        modePanels.forEach(panel => {
            const wantedId = mode === 'one_off'
                ? 'planning-one-off-panel'
                : 'planning-recurring-panel';
            panel.classList.toggle('hidden', panel.id !== wantedId);
        });
    }

    function paintFreq(freq) {
        freqBtns.forEach(btn => {
            const isOn = btn.dataset.planningFreq === freq;
            btn.setAttribute('aria-selected', isOn ? 'true' : 'false');
            ACTIVE_TAB_CLASSES.forEach(c => btn.classList.toggle(c, isOn));
            INACTIVE_TAB_CLASSES.forEach(c => btn.classList.toggle(c, !isOn));
        });
        freqPanels.forEach(panel => {
            panel.classList.toggle('hidden', panel.id !== `planning-freq-${freq}`);
        });
    }

    function paintChips() {
        chips.forEach(chip => {
            const isOn = activeChips.has(chip.dataset.weekdayChip);
            ACTIVE_CHIP_CLASSES.forEach(c => chip.classList.toggle(c, isOn));
            INACTIVE_CHIP_CLASSES.forEach(c => chip.classList.toggle(c, !isOn));
            chip.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        });
    }

    function activeMode() {
        // PR-B step B.4a — read ``checked`` on the native radio rather
        // than the legacy ``aria-pressed`` attribute the segmented-
        // button toggle used to carry.
        const on = modeBtns.find(b => b.checked);
        return on ? on.dataset.planningMode : 'recurring';
    }
    function activeFreq() {
        const on = freqBtns.find(b => b.getAttribute('aria-selected') === 'true');
        return on ? on.dataset.planningFreq : 'weekly';
    }

    /**
     * Source-of-truth function : project the visible UI state into
     * the hidden form fields. Called on every interaction so the POST
     * body is always coherent with what the office sees.
     */
    function setPositions(value) {
        if (hMonthlyPositions) hMonthlyPositions.value = value;
    }

    function syncToHiddenFields() {
        const mode = activeMode();
        if (mode === 'one_off') {
            hRecurrence.value = 'one_off';
            hFrequency.value = '';
            hInterval.value = 1;
            hByWeekday.value = '';
            hByMonth.value = '';
            hStartTime.value = '';
            hMonthlyDay.value = '';
            setPositions('');
            return;
        }
        // Recurring : the only « non-one-off » enum value after
        // migration 0098 collapsed RecurrenceType to {ONE_OFF,
        // RECURRING}. ``_validate_planning_primitives`` is now the
        // single recurrence validator on the form side.
        hRecurrence.value = 'recurring';
        const freq = activeFreq();
        if (freq === 'daily') {
            // « DAILY » is UI sugar for WEEKLY interval=N byweekday=
            // MO..FR (or MO..SU if « include weekends » ticked). PO
            // decision Q2 (see docs/PLANNING_REFACTOR/IMPLEMENTATION_
            // PLAN.md § Décisions).
            hFrequency.value = 'weekly';
            hInterval.value = subfields.dailyInterval?.value || 1;
            const weekdays = subfields.dailyWeekend?.checked
                ? PLANNING_WEEKDAY_CODES
                : PLANNING_WEEKDAY_CODES.filter(c => !PLANNING_WEEKEND_CODES.includes(c));
            hByWeekday.value = weekdays.join(',');
            hByMonth.value = '';
            hStartTime.value = subfields.dailyTime?.value || '';
            hMonthlyDay.value = '';
            setPositions('');
        } else if (freq === 'weekly') {
            hFrequency.value = 'weekly';
            hInterval.value = subfields.weeklyInterval?.value || 1;
            // Preserve user-typed order : sort the codes so the POST
            // is deterministic across reorderings (the server
            // canonicalises via sorted(set(...)) too, but a stable
            // client-side order makes Cypress assertions readable).
            const sorted = PLANNING_WEEKDAY_CODES.filter(c => activeChips.has(c));
            hByWeekday.value = sorted.join(',');
            hByMonth.value = '';
            hStartTime.value = subfields.weeklyTime?.value || '';
            hMonthlyDay.value = '';
            setPositions('');
        } else if (freq === 'twice_monthly') {
            // Monthly-nth-weekday rule : ONE weekday + a positions pair
            // (1,3 or 2,4). Stored server-side as frequency=twice_monthly
            // + byweekday=<one code> + monthly_weekday_positions.
            hFrequency.value = 'twice_monthly';
            hInterval.value = 1;
            hByWeekday.value = subfields.twiceWeekday?.value || '';
            hByMonth.value = '';
            hStartTime.value = subfields.twiceTime?.value || '';
            hMonthlyDay.value = '';
            setPositions(subfields.twicePositions?.value || '');
        } else if (freq === 'monthly') {
            hFrequency.value = 'monthly';
            hInterval.value = subfields.monthlyInterval?.value || 1;
            hByWeekday.value = '';
            hByMonth.value = '';
            hStartTime.value = subfields.monthlyTime?.value || '';
            hMonthlyDay.value = subfields.monthlyDay?.value || '';
            setPositions('');
        } else if (freq === 'yearly') {
            hFrequency.value = 'yearly';
            hInterval.value = 1; // PO decision : yearly always interval=1
            hByWeekday.value = '';
            hByMonth.value = subfields.yearlyMonth?.value || '';
            hStartTime.value = subfields.yearlyTime?.value || '';
            hMonthlyDay.value = subfields.yearlyDay?.value || '';
            setPositions('');
        }
    }

    function setMode(mode) {
        if (!PLANNING_MODES.includes(mode)) mode = 'recurring';
        paintMode(mode);
        syncToHiddenFields();
    }

    function setFreq(freq) {
        if (!PLANNING_FREQUENCIES.includes(freq)) freq = 'weekly';
        paintFreq(freq);
        syncToHiddenFields();
    }

    function toggleChip(code) {
        if (activeChips.has(code)) {
            activeChips.delete(code);
        } else {
            activeChips.add(code);
        }
        paintChips();
        syncToHiddenFields();
    }

    // ─── wire interactions ───────────────────────────────────────
    // PR-B step B.4a — mode toggle is now a radiogroup. The native
    // ``change`` event fires when the user picks the radio (via
    // click, tap, OR keyboard arrow-key navigation in the group),
    // unlike ``click`` which doesn't fire on arrow-key selection.
    modeBtns.forEach(btn => {
        btn.addEventListener('change', () => {
            if (modeLocked) {
                paintMode(hRecurrence.value === 'one_off' ? 'one_off' : 'recurring');
                return;
            }
            if (btn.checked) setMode(btn.dataset.planningMode);
        });
    });
    freqBtns.forEach(btn => {
        btn.addEventListener('click', () => setFreq(btn.dataset.planningFreq));
    });
    chips.forEach(chip => {
        chip.addEventListener('click', () => toggleChip(chip.dataset.weekdayChip));
    });
    Object.values(subfields).forEach(el => {
        if (!el) return;
        // Listen to BOTH events: native text/time/number inputs fire
        // 'input' as the user types, but the custom-select combobox
        // (components/custom-select.js) commits a pick by dispatching
        // a synthetic 'change' ONLY — no 'input'. Binding just 'input'
        // silently missed every <select.form-select> pick (twice-monthly
        // weekday/positions, yearly month): the visible widget updated
        // but the hidden RRULE field kept its old value, so the POST
        // saved the previous weekday. Sync is idempotent and cheap, so
        // the double fire on native selects is harmless.
        el.addEventListener('input', syncToHiddenFields);
        el.addEventListener('change', syncToHiddenFields);
    });

    // ─── hydrate initial state ───────────────────────────────────
    const initialRecurrence = card.dataset.planningInitialRecurrence || '';
    const initialFrequency = card.dataset.planningInitialFrequency || '';
    const initialInterval = parseInt(card.dataset.planningInitialInterval || '1', 10) || 1;
    const initialByWeekday = (card.dataset.planningInitialByweekday || '')
        .split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    const initialByMonth = parseInt(card.dataset.planningInitialBymonth || '0', 10) || 0;
    const initialMonthlyDay = parseInt(card.dataset.planningInitialMonthlyDay || '0', 10) || 0;
    const initialStartTime = card.dataset.planningInitialStartTime || '';
    const initialPositions = (card.dataset.planningInitialPositions || '').trim();

    // 1. Mode : one_off vs recurring. Legacy enum values WEEKLY /
    // BIWEEKLY / MONTHLY all map to « recurring » (the office is
    // editing a backfilled mission — they should still see the
    // recurring panel with the right freq tab pre-selected).
    const mode = initialRecurrence === 'one_off' ? 'one_off' : 'recurring';

    // 2. Freq tab. WEEKLY with all 7 weekdays (or MO..FR + no SA/SU)
    // → DAILY tab (the UI-sugar branch). WEEKLY with a subset →
    // WEEKLY tab. MONTHLY / YEARLY → matching tab.
    let freq;
    if (initialFrequency === 'twice_monthly') {
        freq = 'twice_monthly';
    } else if (initialFrequency === 'monthly') {
        freq = 'monthly';
    } else if (initialFrequency === 'yearly') {
        freq = 'yearly';
    } else if (initialFrequency === 'weekly') {
        const all7 = PLANNING_WEEKDAY_CODES.every(c => initialByWeekday.includes(c));
        const mondayToFriday =
            initialByWeekday.length === 5
            && PLANNING_WEEKEND_CODES.every(c => !initialByWeekday.includes(c));
        freq = (all7 || mondayToFriday) ? 'daily' : 'weekly';
    } else {
        // Legacy backfilled rows whose ``frequency`` is still NULL ;
        // default to WEEKLY tab. The office will pick the right one.
        freq = 'weekly';
    }

    // 3. Hydrate sub-fields. We populate the inputs for ALL four tabs
    // (not just the active one) so a tab switch doesn't reveal stale
    // « 1 » placeholders. The dormant tabs read from the same
    // initial state — switching back later gives a sensible default.
    // No hardcoded fallback: a new mission must have its anchor time
    // explicitly chosen (empty ``--:--`` until then). Pre-filling 09:00
    // risked silently scheduling at the wrong hour when the operator
    // skipped the field. ``start_time`` is required server-side for
    // recurring missions (``_validate_planning_primitives``).
    const defaultTime = initialStartTime;
    if (subfields.dailyInterval) {
        subfields.dailyInterval.value = freq === 'daily' ? initialInterval : 1;
        if (subfields.dailyWeekend) {
            subfields.dailyWeekend.checked =
                freq === 'daily' && initialByWeekday.length === 7;
        }
        if (subfields.dailyTime) subfields.dailyTime.value = defaultTime;
    }
    if (subfields.weeklyInterval) {
        subfields.weeklyInterval.value = freq === 'weekly' ? initialInterval : 1;
        if (subfields.weeklyTime) subfields.weeklyTime.value = defaultTime;
        if (freq === 'weekly') {
            initialByWeekday.forEach(c => activeChips.add(c));
        } else if (freq === 'daily') {
            // DAILY tab implies the chips would be all-on if the
            // office switched to Weekly tab — pre-select them.
            (subfields.dailyWeekend?.checked
                ? PLANNING_WEEKDAY_CODES
                : PLANNING_WEEKDAY_CODES.filter(c => !PLANNING_WEEKEND_CODES.includes(c))
            ).forEach(c => activeChips.add(c));
        } else {
            // Default chip pre-selection on a fresh form : Monday.
            activeChips.add('MO');
        }
    }
    if (subfields.twiceWeekday) {
        // Single weekday : first valid code from the initial byweekday
        // CSV, defaulting to Monday on a fresh form.
        subfields.twiceWeekday.value =
            (freq === 'twice_monthly' && initialByWeekday[0]) || 'MO';
        if (subfields.twicePositions) {
            subfields.twicePositions.value =
                (freq === 'twice_monthly' && initialPositions) || '1,3';
        }
        if (subfields.twiceTime) subfields.twiceTime.value = defaultTime;
    }
    if (subfields.monthlyInterval) {
        subfields.monthlyInterval.value = freq === 'monthly' ? initialInterval : 1;
        if (subfields.monthlyDay) {
            subfields.monthlyDay.value =
                (freq === 'monthly' || freq === 'yearly') && initialMonthlyDay
                    ? initialMonthlyDay
                    : 1;
        }
        if (subfields.monthlyTime) subfields.monthlyTime.value = defaultTime;
    }
    if (subfields.yearlyDay) {
        subfields.yearlyDay.value =
            freq === 'yearly' && initialMonthlyDay ? initialMonthlyDay : 1;
        if (subfields.yearlyMonth) {
            subfields.yearlyMonth.value = freq === 'yearly' && initialByMonth
                ? initialByMonth : 1;
        }
        if (subfields.yearlyTime) subfields.yearlyTime.value = defaultTime;
    }

    paintChips();
    paintFreq(freq);
    paintMode(mode);
    syncToHiddenFields();

    // Notify enhancement layers that hydration changed the selects
    // programmatically. The custom-select combobox (components/
    // custom-select.js) snapshots its visible button label at init and
    // only refreshes it on a ``change`` event — setting ``.value``
    // above is silent, so without this dispatch the combobox keeps
    // showing the template's FIRST option (e.g. « Lundi ») while the
    // native select (and the POST) carry the real persisted weekday.
    // The user then reads a freshly-saved form as « my change was
    // lost ». Dispatching after the final syncToHiddenFields() keeps
    // the re-entrant sync (our own change listener) a no-op.
    Object.values(subfields).forEach(el => {
        if (el && el.tagName === 'SELECT') {
            el.dispatchEvent(new Event('change', { bubbles: true }));
        }
    });
}

/**
 * ``data-auto-init`` handler — invoked by ``app.js::_runAutoInit`` when
 * an element with ``data-auto-init="jobFormPage"`` is found. Reads the
 * ``job-form-page-config`` ``json_script`` block built server-side.
 */
export function autoInitJobFormPage() {
    // PR-B step B.3b — new Planning card interactivity. Bind separately
    // from ``initJobFormPage`` so the card works even if other
    // sections of the form fail to init (e.g. a degraded create-only
    // page). The function is idempotent (per-element flag on
    // ``#planning-card``) so a double-call is a no-op.
    //
    // MUST run BEFORE ``initJobFormPage``: its hydration pass ends with
    // ``syncToHiddenFields()``, which normalises the hidden RRULE
    // fields (e.g. ``start_time`` « 09:00:00 » → « 09:00 », empty
    // ``interval`` → « 1 »). ``initJobEditSubmit`` (called from
    // ``initJobFormPage``) snapshots those same hidden fields for the
    // schedule-change diff — capturing them pre-normalisation would
    // make every untouched edit look like a recurrence change.
    initPlanningCard();
    const config = _readJsonScript('job-form-page-config');
    if (config) initJobFormPage(config);
}
